<template>
  <!-- FIELD BUBBLES UPDATED - COMPACT STYLING APPLIED -->
  <div class="job-mapping-container">
  <div class="job-mapping-tab">
      <div class="job-field-mapping-hint">
        Default two-way field mapping for linked jobs and opportunities is under
        <strong>Match job fields</strong>. Workflows below still create new jobs from {{ brand.agencyName }}.
      </div>
      <div class="workflow-main-content">
        <!-- Workflow List Section -->
        <div class="workflows-header-row">
          <div class="search-container-large">
            <input 
              type="text" 
              v-model="workflowSearchTerm" 
              placeholder="Search..." 
              class="search-input-large"
            />
            <Search class="search-icon-inside" :size="18" />
          </div>
          <button @click="addNewWorkflow" class="btn-add-workflow-large">
            Add New Workflow
        </button>
      </div>

        <div class="config-section workflows-list-section">
          <div v-if="workflows.length === 0" class="no-workflows">
            <p>No workflows configured. Click "Add New Workflow" to create your first mapping workflow.</p>
      </div>

          <div v-else class="workflows-table-container-modern">
            <table class="workflows-list-table-modern">
          <thead>
            <tr>
                  <th>Name</th>
                  <th>Trigger</th>
                  <th>Status</th>
                  <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
                <tr 
                  v-for="(workflow, index) in workflows" 
                  :key="workflow.id" 
                  v-show="!workflowSearchTerm || (workflow.name && workflow.name.toLowerCase().includes(workflowSearchTerm.toLowerCase()))"
                  :class="{ 'editing-row': editingWorkflowIndex === index }"
                  @click="editWorkflow(index)"
                  class="workflow-row-modern"
                >
                  <td class="workflow-name-cell">{{ workflow.name || 'Unnamed Workflow' }}</td>
                        <td class="workflow-trigger-cell">
                          <span v-if="workflow.trigger.when === 'opportunity_status_changed'">Status Changed</span>
                          <span v-else-if="workflow.trigger.when === 'opportunity_stage_changed'">Stage Changed</span>
                        </td>
                  <td class="workflow-status-cell" @click.stop>
                    <div class="status-cell-content">
                    <span class="workflow-badge-modern" :class="workflow.enabled ? 'enabled' : 'disabled'">
                      {{ workflow.enabled ? 'Enabled' : 'Disabled' }}
                </span>
                      <label class="toggle-switch-inline">
                        <input 
                          type="checkbox" 
                          :checked="workflow.enabled"
                          @change="workflow.enabled = !workflow.enabled; saveWorkflow(index)"
                        />
                        <span class="toggle-slider"></span>
                      </label>
                    </div>
              </td>
                  <td class="text-right">
                    <div class="action-buttons-modern">
                      <button @click="editWorkflow(index)" class="btn-table-action" title="Edit Workflow">
                        Edit
                  </button>
                      <div class="dropdown-wrapper" :class="{ 'dropdown-container-active': activeDropdownIndex === index }">
                        <button @click="toggleDropdown(index, $event)" class="btn-more-actions" title="More Actions">
                          <MoreVertical :size="18" />
                  </button>
                        <div v-if="activeDropdownIndex === index" class="dropdown-menu-modern">
                          <button @click="deleteWorkflow(index); activeDropdownIndex = null" class="dropdown-item-modern delete">
                            <Trash :size="14" />
                            Remove
                  </button>
                        </div>
                      </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    </div> <!-- closing workflow-main-content -->

      <!-- Single Workflow Editing Section -->
      <div v-if="editingWorkflowIndex !== null && workflows[editingWorkflowIndex]" class="workflow-edit-section">
        <div class="workflow-editor-container">
          <div class="workflow-card">
            <div class="workflow-header">
              <div class="workflow-title">
                <div class="field-label-row">
                  <label class="field-label" style="margin: 0;">Workflow Name</label>
                </div>
                <div class="workflow-title-row">
                  <RichTextFieldInput 
                    :id="`workflow-name-${editingWorkflowIndex}`"
                    :value="workflows[editingWorkflowIndex].name || ''"
                    @input="(val) => { workflows[editingWorkflowIndex].name = val; saveWorkflow(editingWorkflowIndex); }"
                    @blur="saveWorkflow(editingWorkflowIndex)"
                    :is-over="dragOverField === `workflow-name-${editingWorkflowIndex}`"
                    placeholder="Workflow Name"
                    min-height="36px"
                    :single-line="true"
                    class="workflow-name-input-rich"
                    :example-data="exampleOpportunityData[editingWorkflowIndex]"
                    :show-examples="showingExamples[`workflow-name-${editingWorkflowIndex}`] || false"
                  />
                </div>
              </div>
              <div class="workflow-actions">
                <button @click="editingWorkflowIndex = null; showFieldsPanel = false;" class="btn-close-save">
                  Close & Save
                </button>
              </div>
            </div>

            <div class="workflow-content">
            <!-- Trigger Conditions -->
            <div class="workflow-section">
              <h4>Trigger Conditions</h4>
              <div class="workflow-option">
          <label>
                  <strong>When:</strong>
                  <select v-model="workflows[editingWorkflowIndex].trigger.when" @change="handleTriggerChange(editingWorkflowIndex)" class="select-field">
                    <option value="opportunity_stage_changed">When Opportunity Stage Changed</option>
                    <option value="opportunity_status_changed">Opportunity Status Changed</option>
                  </select>
                </label>
              </div>

              <div v-if="workflows[editingWorkflowIndex].trigger.when === 'opportunity_status_changed'" class="workflow-option">
                <label>
                  <strong>Status:</strong>
                  <select 
                    v-model="workflows[editingWorkflowIndex].trigger.status" 
                    @change="handleTriggerChange(editingWorkflowIndex)" 
                    class="select-field"
                    :class="{ 'invalid-field': !workflows[editingWorkflowIndex].trigger.status }"
                  >
                    <option value="">-- Select Status --</option>
                    <option value="all">Any Status</option>
                    <option value="open">Open</option>
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                    <option value="abandoned">Abandoned</option>
                  </select>
                </label>
              </div>

              <div v-if="workflows[editingWorkflowIndex].trigger.when === 'opportunity_stage_changed'" class="workflow-option">
                <label>
                  <strong>Pipeline:</strong>
                  <select 
                    v-model="workflows[editingWorkflowIndex].trigger.pipelineId" 
                    @change="handlePipelineChange(editingWorkflowIndex)"
                    class="select-field"
                    :class="{ 'invalid-field': !workflows[editingWorkflowIndex].trigger.pipelineId }"
                  >
                    <option value="">Select a pipeline</option>
                    <option v-for="pipeline in pipelines" :key="pipeline.id" :value="pipeline.id">
                      {{ pipeline.name }}
                    </option>
                  </select>
                </label>
                <p class="config-description">Select a pipeline to filter stages</p>
              </div>

              <div v-if="workflows[editingWorkflowIndex].trigger.when === 'opportunity_stage_changed' && workflows[editingWorkflowIndex].trigger.pipelineId" class="workflow-option">
                <label>
                  <strong>Stage:</strong>
                  <select 
                    v-model="workflows[editingWorkflowIndex].trigger.stageId" 
                    @change="handleTriggerChange(editingWorkflowIndex)"
                    class="select-field"
                    :class="{ 'invalid-field': !workflows[editingWorkflowIndex].trigger.stageId }"
                  >
                    <option value="">-- Select Stage --</option>
                    <option value="all">Any Stage</option>
                    <option 
                      v-for="stage in getPipelineStages(workflows[editingWorkflowIndex].trigger.pipelineId)" 
                      :key="stage.id" 
                      :value="stage.id"
                    >
                      {{ stage.name }}
                    </option>
                  </select>
                </label>
                <p class="config-description">Select a specific stage or leave as "Any Stage" to trigger on any stage change in this pipeline</p>
              </div>

              <div v-if="workflows[editingWorkflowIndex].trigger.when === 'opportunity_stage_changed'" class="workflow-option">
                <label class="checkbox-label">
            <input 
              type="checkbox" 
                    v-model="workflows[editingWorkflowIndex].trigger.includeNew" 
                    @change="saveWorkflow(editingWorkflowIndex)"
            />
                  <span>Include New Opportunities</span>
          </label>
                <p class="config-description">If checked, this workflow will also trigger when a new opportunity is created in the selected pipeline/stage.</p>
              </div>
        </div>

            <!-- Example Opportunity -->
          <div 
            v-if="shouldShowExampleOpportunityDropdown(editingWorkflowIndex)" 
            class="workflow-section"
          >
            <h4>Example Opportunity</h4>
            <p class="config-description">Select an example opportunity to preview how your mappings will look with real data.</p>
            
            <div class="workflow-option">
          <label>
                <strong>Select Example:</strong>
                <div class="example-opportunity-controls">
                  <select 
                    :value="selectedExampleOpportunity[editingWorkflowIndex] ? selectedExampleOpportunity[editingWorkflowIndex].id : ''"
                    @change="onExampleOpportunitySelected(editingWorkflowIndex, $event.target.value)"
                    class="select-field"
                    :disabled="loadingExampleOpportunities[editingWorkflowIndex]"
                  >
                    <option value="">-- Select an opportunity --</option>
                    <option 
                      v-for="opp in exampleOpportunities[editingWorkflowIndex] || []" 
                      :key="opp.id" 
                      :value="opp.id"
                    >
                      {{ opp.name || 'Unnamed Opportunity' }} 
                      <span v-if="opp.status">- {{ opp.status }}</span>
                      <span v-else-if="opp.pipelineStageId">- Stage</span>
                    </option>
            </select>
                  <button 
                    @click="loadExampleOpportunities(editingWorkflowIndex)"
                    class="btn-refresh-icon"
                    :disabled="loadingExampleOpportunities[editingWorkflowIndex]"
                    title="Refresh opportunities"
                    :class="{ 'loading': loadingExampleOpportunities[editingWorkflowIndex] }"
                  >
                    <RefreshCw :size="18" />
                  </button>
                </div>
          </label>
              <div v-if="loadingExampleOpportunities[editingWorkflowIndex]" class="loading-message">
                Loading opportunities...
              </div>
              <div v-else-if="exampleOpportunities[editingWorkflowIndex] && exampleOpportunities[editingWorkflowIndex].length === 0" class="no-opportunities-message">
                No matching opportunities found. Try selecting different filters.
              </div>
              <button 
                v-if="exampleOpportunitiesHasMore[editingWorkflowIndex] && !loadingExampleOpportunities[editingWorkflowIndex]"
                @click="loadMoreExampleOpportunities(editingWorkflowIndex)"
                class="btn-load-more"
                :disabled="loadingMoreOpportunities[editingWorkflowIndex]"
              >
                {{ loadingMoreOpportunities[editingWorkflowIndex] ? 'Loading more...' : 'Load More' }}
              </button>
            </div>
          </div>

          <!-- Job Configuration (Only visible after example opportunity is picked) -->
          <div v-if="selectedExampleOpportunity[editingWorkflowIndex]" class="job-config-full-container">
            <div class="job-config-section">
              <h4 class="section-header">Job Configuration</h4>
            
            <div class="config-content">

              <!-- Use Sites Toggle (Hidden for now, can be re-enabled in the future) -->
              <div v-if="false" class="toggle-box-sites mb-6">
                <div class="toggle-container">
                  <div class="toggle-content">
                    <label class="field-label">
                      Use Sites
                    </label>
                    <p class="field-description">
                      Enable to work with ServiceM8 Client Sites. When enabled, only head office companies will be shown in the company search.
                    </p>
                  </div>
                  <label class="toggle-switch">
                    <input 
                      type="checkbox" 
                      v-model="workflows[editingWorkflowIndex].jobConfig.useSiteNameFormat"
                      @change="saveWorkflow(editingWorkflowIndex)"
                    />
                    <span class="toggle-slider"></span>
                  </label>
        </div>

                <!-- Site Configuration fields - appears when Use Sites is toggled ON -->
                <div v-if="workflows[editingWorkflowIndex].jobConfig.useSiteNameFormat" class="site-config-fields">
                  <div class="config-grid">
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">Site Name Format</label>
                        <button 
                          v-if="editingWorkflowIndex !== null"
                          @mousedown="toggleExamplePreview(`site-name-format-${editingWorkflowIndex}`, true)"
                          @mouseup="toggleExamplePreview(`site-name-format-${editingWorkflowIndex}`, false)"
                          @mouseleave="toggleExamplePreview(`site-name-format-${editingWorkflowIndex}`, false)"
                          class="btn-preview-icon"
                          :class="{ 'active': showingExamples[`site-name-format-${editingWorkflowIndex}`] }"
                          :title="!exampleOpportunityData[editingWorkflowIndex] ? 'Select an example opportunity to preview' : 'Click and hold to preview'"
                        >
                          <Search :size="12" />
                        </button>
                      </div>
                      <RichTextFieldInput 
                        :id="`site-name-format-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.siteNameFormat || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.siteNameFormat = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `site-name-format-${editingWorkflowIndex}`"
                        placeholder="Enter site name format"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`site-name-format-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">Property Owner</label>
                        <button 
                          v-if="editingWorkflowIndex !== null"
                          @mousedown="toggleExamplePreview(`property-owner-${editingWorkflowIndex}`, true)"
                          @mouseup="toggleExamplePreview(`property-owner-${editingWorkflowIndex}`, false)"
                          @mouseleave="toggleExamplePreview(`property-owner-${editingWorkflowIndex}`, false)"
                          class="btn-preview-icon"
                          :class="{ 'active': showingExamples[`property-owner-${editingWorkflowIndex}`] }"
                          :title="!exampleOpportunityData[editingWorkflowIndex] ? 'Select an example opportunity to preview' : 'Click and hold to preview'"
                        >
                          <Search :size="12" />
                        </button>
                      </div>
                      <RichTextFieldInput 
                        :id="`property-owner-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.propertyOwner || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.propertyOwner = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `property-owner-${editingWorkflowIndex}`"
                        placeholder="Enter property owner"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`property-owner-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Job Status -->
              <div class="config-field mb-6">
                <label class="field-label">Job Status</label>
                <select 
                  v-model="workflows[editingWorkflowIndex].jobConfig.statusOverride" 
                  @change="saveWorkflow(editingWorkflowIndex)" 
                  class="config-select"
                >
                  <option value="Quote">Quote</option>
                  <option value="Work Order">Work Order</option>
              <option value="Completed">Completed</option>
                  <option value="Unsucessful">Unsucessful</option>
            </select>
                <p class="field-description">
                  All jobs created from this template will default to "{{ workflows[editingWorkflowIndex].jobConfig.statusOverride || 'Quote' }}"
          </p>
        </div>

              <!-- Main Grid: Company, PO Number, Category, Badges, Billing Address -->
              <div class="config-grid">
                <!-- Client -->
                <div class="config-field">
                  <div class="field-label-row">
                    <label class="field-label">Client</label>
                    <button 
                      v-if="editingWorkflowIndex !== null"
                      @mousedown="toggleExamplePreview(`company-${editingWorkflowIndex}`, true)"
                      @mouseup="toggleExamplePreview(`company-${editingWorkflowIndex}`, false)"
                      @mouseleave="toggleExamplePreview(`company-${editingWorkflowIndex}`, false)"
                      class="btn-preview-icon"
                      :class="{ 'active': showingExamples[`company-${editingWorkflowIndex}`] }"
                      :title="!exampleOpportunityData[editingWorkflowIndex] ? 'Select an example opportunity to preview' : 'Click and hold to preview'"
                    >
                      <Search :size="12" />
                    </button>
                  </div>
                  <RichTextFieldInput 
                    :id="`company-${editingWorkflowIndex}`"
                    :value="workflows[editingWorkflowIndex].jobConfig.company || ''"
                    @input="(val) => { workflows[editingWorkflowIndex].jobConfig.company = val; saveWorkflow(editingWorkflowIndex); }"
                    @blur="saveWorkflow(editingWorkflowIndex)"
                    :is-over="dragOverField === `company-${editingWorkflowIndex}`"
                    placeholder="Select company"
                    min-height="36px"
                    :single-line="true"
                    :example-data="exampleOpportunityData[editingWorkflowIndex]"
                    :show-examples="showingExamples[`company-${editingWorkflowIndex}`] || false"
                  />
                </div>

                <!-- PO Number -->
                <div class="config-field">
                  <div class="field-label-row">
                    <label class="field-label">PO Number</label>
                    <button 
                      v-if="editingWorkflowIndex !== null"
                      @mousedown="toggleExamplePreview(`po-number-${editingWorkflowIndex}`, true)"
                      @mouseup="toggleExamplePreview(`po-number-${editingWorkflowIndex}`, false)"
                      @mouseleave="toggleExamplePreview(`po-number-${editingWorkflowIndex}`, false)"
                      class="btn-preview-icon"
                      :class="{ 'active': showingExamples[`po-number-${editingWorkflowIndex}`] }"
                      :title="!exampleOpportunityData[editingWorkflowIndex] ? 'Select an example opportunity to preview' : 'Click and hold to preview'"
                    >
                      <Search :size="12" />
                    </button>
                  </div>
                  <RichTextFieldInput 
                    :id="`po-number-${editingWorkflowIndex}`"
                    :value="workflows[editingWorkflowIndex].jobConfig.poNumber || ''"
                    @input="(val) => { workflows[editingWorkflowIndex].jobConfig.poNumber = val; saveWorkflow(editingWorkflowIndex); }"
                    @blur="saveWorkflow(editingWorkflowIndex)"
                    :is-over="dragOverField === `po-number-${editingWorkflowIndex}`"
                    placeholder="Enter PO number"
                    min-height="36px"
                    :single-line="true"
                    :example-data="exampleOpportunityData[editingWorkflowIndex]"
                    :show-examples="showingExamples[`po-number-${editingWorkflowIndex}`] || false"
                  />
                </div>

                <!-- Job Category -->
                <div class="config-field">
                  <label class="field-label">Job Category</label>
                  <select 
                    v-model="workflows[editingWorkflowIndex].jobConfig.categoryUuid" 
                    @change="saveWorkflow(editingWorkflowIndex)" 
                    class="config-select"
                  >
                    <option value="">Select a category</option>
                    <option v-for="category in categories" :key="category.uuid" :value="category.uuid">
                      {{ category.name }}
                    </option>
              </select>
                </div>

                <!-- Badges -->
                <div class="config-field">
                  <label class="field-label">Badges</label>
                  <select 
                    v-model="workflows[editingWorkflowIndex].jobConfig.badges" 
                    @change="saveWorkflow(editingWorkflowIndex)" 
                    class="config-select"
                  >
                    <option value="">Select a badge</option>
                    <option v-for="badge in badges" :key="badge.uuid" :value="badge.uuid">
                      {{ badge.name }}
                    </option>
              </select>
                </div>

                <!-- Billing Address -->
                <div class="config-field full-width">
                  <div class="billing-address-container">
                    <div class="checkbox-row">
                      <label class="checkbox-label">
                        <input 
                          type="checkbox" 
                          v-model="workflows[editingWorkflowIndex].jobConfig.useCompanyBillingAddress"
                          @change="saveWorkflow(editingWorkflowIndex)"
                        />
                        Use company/head office billing address
                      </label>
                      <button 
                        v-if="editingWorkflowIndex !== null"
                        @mousedown="toggleExamplePreview(`billing-address-${editingWorkflowIndex}`, true)"
                        @mouseup="toggleExamplePreview(`billing-address-${editingWorkflowIndex}`, false)"
                        @mouseleave="toggleExamplePreview(`billing-address-${editingWorkflowIndex}`, false)"
                        class="btn-preview-icon"
                        :class="{ 'active': showingExamples[`billing-address-${editingWorkflowIndex}`] }"
                        :title="!exampleOpportunityData[editingWorkflowIndex] ? 'Select an example opportunity to preview' : 'Click and hold to preview'"
                      >
                        <Search :size="12" />
              </button>
            </div>
                    <RichTextFieldInput
                      :id="`billing-address-${editingWorkflowIndex}`"
                      :value="workflows[editingWorkflowIndex].jobConfig.billingAddress || ''"
                      @input="(val) => { workflows[editingWorkflowIndex].jobConfig.billingAddress = val; saveWorkflow(editingWorkflowIndex); }"
                      @blur="saveWorkflow(editingWorkflowIndex)"
                      :is-over="dragOverField === `billing-address-${editingWorkflowIndex}`"
                      :class="{ 'disabled': workflows[editingWorkflowIndex].jobConfig.useCompanyBillingAddress && !workflows[editingWorkflowIndex].jobConfig.billingAddress }"
                      placeholder="Enter billing address"
                      min-height="80px"
                      :example-data="exampleOpportunityData[editingWorkflowIndex]"
                      :show-examples="showingExamples[`billing-address-${editingWorkflowIndex}`] || false"
                    />
                  </div>
                </div>
              </div>
          </div>
        </div>

          <!-- Job Details Section -->
          <div class="job-config-section">
            <h4 class="section-header">Job Details</h4>
            
            <div class="config-content">
              <div class="config-grid">
                <!-- Job Address -->
                <div class="config-field full-width">
                  <div class="field-label-row">
                    <label class="field-label">Job Address</label>
                    <button 
                      v-if="editingWorkflowIndex !== null"
                      @mousedown="toggleExamplePreview(`job-address-${editingWorkflowIndex}`, true)"
                      @mouseup="toggleExamplePreview(`job-address-${editingWorkflowIndex}`, false)"
                      @mouseleave="toggleExamplePreview(`job-address-${editingWorkflowIndex}`, false)"
                      class="btn-preview-icon"
                      :class="{ 'active': showingExamples[`job-address-${editingWorkflowIndex}`] }"
                      :title="!exampleOpportunityData[editingWorkflowIndex] ? 'Select an example opportunity to preview' : 'Click and hold to preview'"
                    >
                      <Search :size="12" />
          </button>
            </div>
                  <RichTextFieldInput
                    :id="`job-address-${editingWorkflowIndex}`"
                    :value="workflows[editingWorkflowIndex].jobConfig.jobAddress || ''"
                    @input="(val) => { workflows[editingWorkflowIndex].jobConfig.jobAddress = val; saveWorkflow(editingWorkflowIndex); }"
                    @blur="saveWorkflow(editingWorkflowIndex)"
                    :is-over="dragOverField === `job-address-${editingWorkflowIndex}`"
                    placeholder="Enter address"
                    min-height="80px"
                    :example-data="exampleOpportunityData[editingWorkflowIndex]"
                    :show-examples="showingExamples[`job-address-${editingWorkflowIndex}`] || false"
                  />
                </div>

                <!-- Job Description -->
                <div class="config-field full-width">
                  <div class="field-label-row">
                    <label class="field-label">Job Description</label>
                    <button 
                      v-if="editingWorkflowIndex !== null"
                      @mousedown="toggleExamplePreview(`job-description-${editingWorkflowIndex}`, true)"
                      @mouseup="toggleExamplePreview(`job-description-${editingWorkflowIndex}`, false)"
                      @mouseleave="toggleExamplePreview(`job-description-${editingWorkflowIndex}`, false)"
                      class="btn-preview-icon"
                      :class="{ 'active': showingExamples[`job-description-${editingWorkflowIndex}`] }"
                      :title="!exampleOpportunityData[editingWorkflowIndex] ? 'Select an example opportunity to preview' : 'Click and hold to preview'"
                    >
                      <Search :size="12" />
          </button>
                  </div>
                  <RichTextFieldInput
                    :id="`job-description-${editingWorkflowIndex}`"
                    :value="(workflows[editingWorkflowIndex].jobConfig.jobDescription && typeof workflows[editingWorkflowIndex].jobConfig.jobDescription === 'string') ? workflows[editingWorkflowIndex].jobConfig.jobDescription : ''"
                    @input="(val) => { workflows[editingWorkflowIndex].jobConfig.jobDescription = val; saveWorkflow(editingWorkflowIndex); }"
                    @blur="saveWorkflow(editingWorkflowIndex)"
                    :is-over="dragOverField === `job-description-${editingWorkflowIndex}`"
                    placeholder="Enter description or drag fields here"
                    min-height="100px"
                    :example-data="exampleOpportunityData[editingWorkflowIndex]"
                    :show-examples="showingExamples[`job-description-${editingWorkflowIndex}`] || false"
                  />
                </div>
              </div>
          </div>
        </div>

          <!-- Contact Information Section -->
          <div class="job-config-section">
            <h4 class="section-header">Contact Information</h4>
            
            <div class="config-content">
              <!-- Job Contact -->
              <div class="contact-section">
                <div class="contact-header">
                  <h5 class="contact-title">Job Contact</h5>
                  <div class="contact-header-actions">
                    <button 
                      v-if="workflows[editingWorkflowIndex].jobConfig.showJobContact"
                      @mousedown="toggleExamplePreview('job-contact', true)"
                      @mouseup="toggleExamplePreview('job-contact', false)"
                      @mouseleave="toggleExamplePreview('job-contact', false)"
                      @touchstart="toggleExamplePreview('job-contact', true)"
                      @touchend="toggleExamplePreview('job-contact', false)"
                      @touchcancel="toggleExamplePreview('job-contact', false)"
                      class="btn-preview-icon"
                      :class="{ 'active': showingExamples['job-contact'] }"
                      title="Hold to preview all job contact fields"
                    >
                      <Search :size="12" />
                    </button>
                    <button 
                      v-if="!workflows[editingWorkflowIndex].jobConfig.showJobContact"
                      @click="workflows[editingWorkflowIndex].jobConfig.showJobContact = true; saveWorkflow(editingWorkflowIndex)"
                      class="btn-add-contact"
                    >
                      + Job Contact
                    </button>
                    <button 
                      v-else
                      @click="workflows[editingWorkflowIndex].jobConfig.showJobContact = false; saveWorkflow(editingWorkflowIndex)"
                      class="btn-trash-icon"
                    >
                      <Trash />
                    </button>
                  </div>
                </div>

                <div v-if="workflows[editingWorkflowIndex].jobConfig.showJobContact" class="contact-content">
                  <div class="checkbox-row mb-4">
                    <label class="checkbox-label">
                      <input 
                        type="checkbox" 
                        v-model="workflows[editingWorkflowIndex].jobConfig.useStoredJobContact"
                        @change="saveWorkflow(editingWorkflowIndex)"
                      />
                      Use Stored Company Contact
                    </label>
      </div>

                  <div v-if="workflows[editingWorkflowIndex].jobConfig.useStoredJobContact" class="mb-4">
                    <select 
                      v-model="workflows[editingWorkflowIndex].jobConfig.selectedJobContact" 
                      @change="saveWorkflow(editingWorkflowIndex)" 
                      class="config-select"
                    >
                      <option value="">Select a job contact</option>
                      <!-- Contacts will be populated later -->
                    </select>
                  </div>

                  <div class="contact-fields-grid">
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">First Name</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`job-contact-first-name-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.jobContactFirstName || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.jobContactFirstName = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `job-contact-first-name-${editingWorkflowIndex}`"
                        placeholder="Enter first name"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`job-contact-first-name-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">Last Name</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`job-contact-last-name-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.jobContactLastName || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.jobContactLastName = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `job-contact-last-name-${editingWorkflowIndex}`"
                        placeholder="Enter last name"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`job-contact-last-name-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">Phone</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`job-contact-phone-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.jobContactPhone || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.jobContactPhone = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `job-contact-phone-${editingWorkflowIndex}`"
                        placeholder="Enter phone"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`job-contact-phone-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">Mobile</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`job-contact-mobile-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.jobContactMobile || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.jobContactMobile = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `job-contact-mobile-${editingWorkflowIndex}`"
                        placeholder="Enter mobile"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`job-contact-mobile-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                    <div class="config-field full-width">
                      <div class="field-label-row">
                        <label class="field-label">Email</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`job-contact-email-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.jobContactEmail || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.jobContactEmail = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `job-contact-email-${editingWorkflowIndex}`"
                        placeholder="Enter email"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`job-contact-email-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Property Manager -->
              <div class="contact-section">
                <div class="contact-header">
                  <h5 class="contact-title">Property Manager</h5>
                  <div class="contact-header-actions">
                    <button 
                      v-if="workflows[editingWorkflowIndex].jobConfig.showPropertyManager"
                      @mousedown="toggleExamplePreview('property-manager', true)"
                      @mouseup="toggleExamplePreview('property-manager', false)"
                      @mouseleave="toggleExamplePreview('property-manager', false)"
                      @touchstart="toggleExamplePreview('property-manager', true)"
                      @touchend="toggleExamplePreview('property-manager', false)"
                      @touchcancel="toggleExamplePreview('property-manager', false)"
                      class="btn-preview-icon"
                      :class="{ 'active': showingExamples['property-manager'] }"
                      title="Hold to preview all property manager fields"
                    >
                      <Search :size="12" />
                    </button>
                    <button 
                      v-if="!workflows[editingWorkflowIndex].jobConfig.showPropertyManager"
                      @click="workflows[editingWorkflowIndex].jobConfig.showPropertyManager = true; saveWorkflow(editingWorkflowIndex)"
                      class="btn-add-contact"
                    >
                      + Property Manager
                    </button>
                    <button 
                      v-else
                      @click="workflows[editingWorkflowIndex].jobConfig.showPropertyManager = false; saveWorkflow(editingWorkflowIndex)"
                      class="btn-trash-icon"
                    >
                      <Trash />
                    </button>
                  </div>
                </div>

                <div v-if="workflows[editingWorkflowIndex].jobConfig.showPropertyManager" class="contact-content">
                  <div class="checkbox-row mb-4">
                    <label class="checkbox-label">
                      <input 
                        type="checkbox" 
                        v-model="workflows[editingWorkflowIndex].jobConfig.useStoredPropertyManager"
                        @change="saveWorkflow(editingWorkflowIndex)"
                      />
                      Use Stored Company Contact
                    </label>
                  </div>

                  <div v-if="workflows[editingWorkflowIndex].jobConfig.useStoredPropertyManager" class="mb-4">
                    <select 
                      v-model="workflows[editingWorkflowIndex].jobConfig.selectedPropertyManager" 
                      @change="saveWorkflow(editingWorkflowIndex)" 
                      class="config-select"
                    >
                      <option value="">Select a property manager</option>
                      <!-- Contacts will be populated later -->
                    </select>
                  </div>

                  <div class="contact-fields-grid">
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">First Name</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`pm-first-name-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.propertyManagerFirstName || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.propertyManagerFirstName = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `pm-first-name-${editingWorkflowIndex}`"
                        placeholder="Enter first name"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`pm-first-name-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">Last Name</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`pm-last-name-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.propertyManagerLastName || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.propertyManagerLastName = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `pm-last-name-${editingWorkflowIndex}`"
                        placeholder="Enter last name"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`pm-last-name-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">Phone</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`pm-phone-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.propertyManagerPhone || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.propertyManagerPhone = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `pm-phone-${editingWorkflowIndex}`"
                        placeholder="Enter phone"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`pm-phone-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">Mobile</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`pm-mobile-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.propertyManagerMobile || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.propertyManagerMobile = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `pm-mobile-${editingWorkflowIndex}`"
                        placeholder="Enter mobile"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`pm-mobile-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                    <div class="config-field full-width">
                      <div class="field-label-row">
                        <label class="field-label">Email</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`pm-email-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.propertyManagerEmail || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.propertyManagerEmail = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `pm-email-${editingWorkflowIndex}`"
                        placeholder="Enter email"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`pm-email-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Billing Contact -->
              <div class="contact-section">
                <div class="contact-header">
                  <h5 class="contact-title">Billing Contact</h5>
                  <div class="contact-header-actions">
                    <button 
                      v-if="workflows[editingWorkflowIndex].jobConfig.showBillingContact"
                      @mousedown="toggleExamplePreview('billing-contact', true)"
                      @mouseup="toggleExamplePreview('billing-contact', false)"
                      @mouseleave="toggleExamplePreview('billing-contact', false)"
                      @touchstart="toggleExamplePreview('billing-contact', true)"
                      @touchend="toggleExamplePreview('billing-contact', false)"
                      @touchcancel="toggleExamplePreview('billing-contact', false)"
                      class="btn-preview-icon"
                      :class="{ 'active': showingExamples['billing-contact'] }"
                      title="Hold to preview all billing contact fields"
                    >
                      <Search :size="12" />
                    </button>
                    <button 
                      v-if="!workflows[editingWorkflowIndex].jobConfig.showBillingContact"
                      @click="workflows[editingWorkflowIndex].jobConfig.showBillingContact = true; saveWorkflow(editingWorkflowIndex)"
                      class="btn-add-contact"
                    >
                      + Billing Contact
                    </button>
                    <button 
                      v-else
                      @click="workflows[editingWorkflowIndex].jobConfig.showBillingContact = false; saveWorkflow(editingWorkflowIndex)"
                      class="btn-trash-icon"
                    >
                      <Trash />
                    </button>
                  </div>
                </div>

                <div v-if="workflows[editingWorkflowIndex].jobConfig.showBillingContact" class="contact-content">
                  <div class="checkbox-row mb-4">
                    <label class="checkbox-label">
                      <input 
                        type="checkbox" 
                        v-model="workflows[editingWorkflowIndex].jobConfig.useStoredBillingContact"
                        @change="saveWorkflow(editingWorkflowIndex)"
                      />
                      Use Stored Company Contact
                    </label>
                  </div>

                  <div v-if="workflows[editingWorkflowIndex].jobConfig.useStoredBillingContact" class="mb-4">
                    <select 
                      v-model="workflows[editingWorkflowIndex].jobConfig.selectedBillingContact" 
                      @change="saveWorkflow(editingWorkflowIndex)" 
                      class="config-select"
                    >
                      <option value="">Select a billing contact</option>
                      <!-- Contacts will be populated later -->
                    </select>
                  </div>

                  <div class="contact-fields-grid">
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">First Name</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`billing-contact-first-name-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.billingContactFirstName || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.billingContactFirstName = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `billing-contact-first-name-${editingWorkflowIndex}`"
                        placeholder="Enter first name"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`billing-contact-first-name-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">Last Name</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`billing-contact-last-name-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.billingContactLastName || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.billingContactLastName = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `billing-contact-last-name-${editingWorkflowIndex}`"
                        placeholder="Enter last name"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`billing-contact-last-name-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">Phone</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`billing-contact-phone-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.billingContactPhone || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.billingContactPhone = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `billing-contact-phone-${editingWorkflowIndex}`"
                        placeholder="Enter phone"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`billing-contact-phone-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                    <div class="config-field">
                      <div class="field-label-row">
                        <label class="field-label">Mobile</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`billing-contact-mobile-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.billingContactMobile || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.billingContactMobile = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `billing-contact-mobile-${editingWorkflowIndex}`"
                        placeholder="Enter mobile"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`billing-contact-mobile-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                    <div class="config-field full-width">
                      <div class="field-label-row">
                        <label class="field-label">Email</label>
                      </div>
                      <RichTextFieldInput 
                        :id="`billing-contact-email-${editingWorkflowIndex}`"
                        :value="workflows[editingWorkflowIndex].jobConfig.billingContactEmail || ''"
                        @input="(val) => { workflows[editingWorkflowIndex].jobConfig.billingContactEmail = val; saveWorkflow(editingWorkflowIndex); }"
                        @blur="saveWorkflow(editingWorkflowIndex)"
                        :is-over="dragOverField === `billing-contact-email-${editingWorkflowIndex}`"
                        placeholder="Enter email"
                        min-height="36px"
                        :single-line="true"
                        :example-data="exampleOpportunityData[editingWorkflowIndex]"
                        :show-examples="showingExamples[`billing-contact-email-${editingWorkflowIndex}`] || false"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Queue Section -->
          <div class="job-config-section">
            <h4 class="section-header">Queue</h4>
            
            <div class="config-content">
              <div class="contact-section">
                <div class="contact-header">
                  <h5 class="contact-title">Queue</h5>
                  <button 
                    v-if="!workflows[editingWorkflowIndex].jobConfig.showQueue"
                    @click="workflows[editingWorkflowIndex].jobConfig.showQueue = true; saveWorkflow(editingWorkflowIndex)"
                    class="btn-add-contact"
                  >
                    + Queue
                  </button>
                  <button 
                    v-else
                    @click="workflows[editingWorkflowIndex].jobConfig.showQueue = false; workflows[editingWorkflowIndex].jobConfig.selectedQueue = null; saveWorkflow(editingWorkflowIndex)"
                    class="justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent h-9 rounded-md px-3 flex items-center gap-2 text-red-500 hover:text-red-700"
                  >
                    <Trash />
                  </button>
                </div>

              <div v-if="workflows[editingWorkflowIndex].jobConfig.showQueue" class="queue-content">
                <div class="config-field">
                  <label class="field-label">Select Queue</label>
                  <select 
                    v-model="workflows[editingWorkflowIndex].jobConfig.selectedQueue" 
                    @change="handleQueueChange(editingWorkflowIndex)" 
                    class="config-select"
                  >
                    <option value="">Select a queue</option>
                    <option v-for="queue in queues" :key="queue.uuid" :value="queue.uuid">
                      {{ queue.name }}
                    </option>
                  </select>
                </div>

                <div v-if="workflows[editingWorkflowIndex].jobConfig.selectedQueue" class="config-field">
                  <label class="field-label">Queue Expiry</label>
                  <div class="expiry-input-group">
                    <input 
                      type="number"
                      v-model.number="workflows[editingWorkflowIndex].jobConfig.queueExpiry"
                      @blur="saveWorkflow(editingWorkflowIndex)"
                      class="config-input expiry-input"
                      min="1"
                    />
                    <span class="expiry-label">Days</span>
                  </div>
                </div>

                <div v-if="workflows[editingWorkflowIndex].jobConfig.selectedQueue && workflows[editingWorkflowIndex].jobConfig.queueRequiresAssignment" class="config-field">
                  <label class="field-label">Assigned Staff</label>
                  <select 
                    v-model="workflows[editingWorkflowIndex].jobConfig.selectedStaff" 
                    @change="saveWorkflow(editingWorkflowIndex)" 
                    class="config-select"
                  >
                    <option value="">Select staff member</option>
                    <option v-for="member in staff" :key="member.uuid" :value="member.uuid">
                      {{ member.first }} {{ member.last }}
                    </option>
                  </select>
                </div>
              </div>
              </div>
            </div>
          </div>

          <!-- Checklists Section -->
          <div class="job-config-section">
            <div class="section-header-with-action">
            <h4 class="section-header">Checklists</h4>
                <button 
                  @click="addChecklistToWorkflow(editingWorkflowIndex)"
                class="btn-add-small"
                >
                  + Checklist
                </button>
              </div>
            
            <div class="config-content">

              <div class="checklists-list">
                <div 
                  v-for="(checklist, checklistIndex) in workflows[editingWorkflowIndex].jobConfig.checklists" 
                  :key="checklist.id"
                  class="checklist-item"
                >
                  <div class="checklist-item-header">
                    <div v-if="editingChecklistId === checklist.id" class="checklist-name-edit">
                      <input 
                        type="text" 
                        v-model="checklist.checklist_name" 
                        @blur="finishChecklistNameEdit(editingWorkflowIndex)"
                        @keydown.enter="finishChecklistNameEdit(editingWorkflowIndex)"
                        class="checklist-name-input-inline"
                        placeholder="Checklist name"
                        v-focus
                      />
                    </div>
                    <div v-else class="checklist-name-display" @click="startChecklistNameEdit(checklist.id)">
                      {{ checklist.checklist_name || 'Unnamed Checklist (click to edit)' }}
                    </div>
                    <div class="dropdown-wrapper" :class="{ 'dropdown-container-active': activeChecklistDropdown && activeChecklistDropdown.workflowIndex === editingWorkflowIndex && activeChecklistDropdown.checklistIndex === checklistIndex }">
                      <button @click.stop="toggleChecklistDropdown(editingWorkflowIndex, checklistIndex, $event)" class="btn-more-actions" title="More Actions">
                        <MoreVertical :size="18" />
                      </button>
                      <div v-if="activeChecklistDropdown && activeChecklistDropdown.workflowIndex === editingWorkflowIndex && activeChecklistDropdown.checklistIndex === checklistIndex" class="dropdown-menu-modern">
                        <button @click="removeChecklistFromWorkflow(editingWorkflowIndex, checklistIndex); activeChecklistDropdown = null" class="dropdown-item-modern delete">
                          Delete Checklist
                    </button>
                      </div>
                    </div>
                  </div>
                  <div class="checklist-items">
                    <div 
                      v-for="(item, itemIndex) in checklist.items" 
                      :key="item.id"
                      class="checklist-item-row"
                    >
                      <input 
                        type="checkbox"
                        v-model="item.completed"
                        @change="saveWorkflow(editingWorkflowIndex)"
                      />
                      <div v-if="newlyAddedItemId === item.id || editingItemId === item.id" class="checklist-item-edit">
                        <input 
                          type="text" 
                          v-model="item.name" 
                          @blur="finishItemNameEdit(editingWorkflowIndex)"
                          @keydown.enter="finishItemNameEdit(editingWorkflowIndex)"
                          @keydown.tab="finishItemNameEdit(editingWorkflowIndex)"
                          class="checklist-item-input-inline"
                          placeholder="Enter task name..."
                          v-focus
                        />
                      </div>
                      <div v-else class="checklist-item-display" @click="startItemNameEdit(item.id)">
                        {{ item.name || 'Unnamed Task (click to edit)' }}
                      </div>
                      <div class="checklist-item-actions">
                      <button 
                          @click.stop="openItemSettings(editingWorkflowIndex, checklistIndex, itemIndex)"
                        class="btn-settings-small"
                        title="Item Settings"
                      >
                        ⚙️
                      </button>
                        <button 
                          @click.stop="removeChecklistItemFromWorkflow(editingWorkflowIndex, checklistIndex, itemIndex)"
                          class="btn-delete-item"
                          title="Delete Item"
                        >
                          <Trash :size="16" />
                        </button>
                      </div>
                    </div>
                    <button 
                      @click="addChecklistItemToWorkflow(editingWorkflowIndex, checklistIndex)"
                      class="btn-add-item"
                    >
                      + New Item
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Notes Section -->
          <div class="job-config-section">
            <div class="section-header-with-action">
            <h4 class="section-header">Notes</h4>
                <button 
                  @click="addNoteToWorkflow(editingWorkflowIndex)"
                class="btn-add-small"
                >
                  + Add Note
                </button>
              </div>
            
            <div class="config-content">

              <div v-if="workflows[editingWorkflowIndex].jobConfig.notes.length === 0" class="empty-state">
                <p>No notes added yet. Click "Add Note" to get started.</p>
              </div>

              <div v-else class="notes-list">
                <div 
                  v-for="(note, noteIndex) in workflows[editingWorkflowIndex].jobConfig.notes" 
                  :key="noteIndex"
                  class="note-item"
                >
                  <div class="field-label-row">
                    <label class="field-label">Note {{ noteIndex + 1 }}</label>
                    <button 
                      v-if="editingWorkflowIndex !== null"
                      @mousedown="toggleExamplePreview(`note-${editingWorkflowIndex}-${noteIndex}`, true)"
                      @mouseup="toggleExamplePreview(`note-${editingWorkflowIndex}-${noteIndex}`, false)"
                      @mouseleave="toggleExamplePreview(`note-${editingWorkflowIndex}-${noteIndex}`, false)"
                      class="btn-preview-icon"
                      :class="{ 'active': showingExamples[`note-${editingWorkflowIndex}-${noteIndex}`] }"
                      :title="!exampleOpportunityData[editingWorkflowIndex] ? 'Select an example opportunity to preview' : 'Click and hold to preview'"
                    >
                      <Search :size="12" />
                    </button>
                  </div>
                  <RichTextFieldInput
                    :id="`note-${editingWorkflowIndex}-${noteIndex}`"
                    :value="(workflows[editingWorkflowIndex].jobConfig.notes[noteIndex] && typeof workflows[editingWorkflowIndex].jobConfig.notes[noteIndex] === 'string') ? workflows[editingWorkflowIndex].jobConfig.notes[noteIndex] : ''"
                    @input="(val) => { workflows[editingWorkflowIndex].jobConfig.notes[noteIndex] = val; saveWorkflow(editingWorkflowIndex); }"
                    @blur="saveWorkflow(editingWorkflowIndex)"
                    :example-data="exampleOpportunityData[editingWorkflowIndex]"
                    :show-examples="showingExamples[`note-${editingWorkflowIndex}-${noteIndex}`] || false"
                    :is-over="dragOverField === `note-${editingWorkflowIndex}-${noteIndex}`"
                    placeholder="Enter note or drag fields here"
                    min-height="80px"
                  />
                  <button 
                    @click="removeNoteFromWorkflow(editingWorkflowIndex, noteIndex)"
                    class="justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent h-9 rounded-md px-3 flex items-center gap-2 text-red-500 hover:text-red-700"
                  >
                    <Trash />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Custom Fields Section -->
          <div class="job-config-section">
            <div class="section-header-with-action">
            <h4 class="section-header">Custom Fields</h4>
                <button 
                  @click="addCustomFieldToWorkflow(editingWorkflowIndex)"
                class="btn-add-small"
                >
                  + Custom Field
                </button>
              </div>
            
            <div class="config-content">

              <div v-if="workflows[editingWorkflowIndex].jobConfig.customFields.length === 0" class="empty-state">
                <p>No custom fields added yet. Click "Custom Field" to add one.</p>
              </div>

              <div v-else class="custom-fields-list">
                <div 
                  v-for="(customField, fieldIndex) in workflows[editingWorkflowIndex].jobConfig.customFields" 
                  :key="customField.id"
                  class="custom-field-item"
                >
                  <div class="custom-field-header">
                    <select 
                      v-model="customField.fieldUuid" 
                      @change="handleCustomFieldChange(editingWorkflowIndex, fieldIndex)" 
                      class="config-select"
                    >
                      <option value="">Select a custom field</option>
                      <option 
                        v-for="field in availableCustomFields" 
                        :key="field.uuid" 
                        :value="field.uuid"
                        :disabled="isCustomFieldSelected(editingWorkflowIndex, field.uuid, customField.fieldUuid)"
                      >
                        {{ field.name }}
                      </option>
                    </select>
                    <button 
                      @click="removeCustomFieldFromWorkflow(editingWorkflowIndex, fieldIndex)"
                      class="justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent h-9 rounded-md px-3 flex items-center gap-2 text-red-500 hover:text-red-700"
                    >
                      <Trash />
                    </button>
                  </div>
                  <div v-if="customField.fieldUuid" class="custom-field-value">
                    <div class="field-label-row">
                      <label class="field-label">{{ customField.name }} Value</label>
                      <button 
                        v-if="editingWorkflowIndex !== null"
                        @mousedown="toggleExamplePreview(`custom-field-${editingWorkflowIndex}-${fieldIndex}`, true)"
                        @mouseup="toggleExamplePreview(`custom-field-${editingWorkflowIndex}-${fieldIndex}`, false)"
                        @mouseleave="toggleExamplePreview(`custom-field-${editingWorkflowIndex}-${fieldIndex}`, false)"
                        class="btn-preview-icon"
                        :class="{ 'active': showingExamples[`custom-field-${editingWorkflowIndex}-${fieldIndex}`] }"
                        :title="!exampleOpportunityData[editingWorkflowIndex] ? 'Select an example opportunity to preview' : 'Click and hold to preview'"
                      >
                        <Search :size="12" />
                      </button>
                    </div>
                    <RichTextFieldInput 
                      :id="`custom-field-${editingWorkflowIndex}-${fieldIndex}`"
                      :value="customField.value || ''"
                      @input="(val) => { customField.value = val; saveWorkflow(editingWorkflowIndex); }"
                      @blur="saveWorkflow(editingWorkflowIndex)"
                      :is-over="dragOverField === `custom-field-${editingWorkflowIndex}-${fieldIndex}`"
                      :placeholder="`Enter ${customField.name} or drag fields`"
                      min-height="36px"
                      :single-line="true"
                      :example-data="exampleOpportunityData[editingWorkflowIndex]"
                      :show-examples="showingExamples[`custom-field-${editingWorkflowIndex}-${fieldIndex}`] || false"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Attachments Section -->
          <div class="job-config-section">
            <h4 class="section-header">Attachments</h4>
            
            <div class="config-content">
              <div class="attachment-sync-options">
                <label class="checkbox-label mb-4">
                  <input 
                    type="checkbox" 
                    v-model="workflows[editingWorkflowIndex].jobConfig.syncAllAttachments"
                    @change="saveWorkflow(editingWorkflowIndex)"
                  />
                  <span>Automatically sync all detected files</span>
                </label>
                <p class="config-description small mb-4">
                  If enabled, any file upload fields containing data will be automatically synced to ServiceM8.
                </p>

                <div v-if="attachmentCapableFields.length > 0" class="attachment-fields-list">
                  <h5 class="fields-section-title">Select specific fields to sync:</h5>
                  <div class="attachment-grid">
                    <label 
                      v-for="field in attachmentCapableFields" 
                      :key="field.uuid" 
                      class="attachment-checkbox-item"
                    >
                      <input 
                        type="checkbox" 
                        :checked="isAttachmentFieldChecked(editingWorkflowIndex, field.uuid)"
                        @change="toggleAttachmentField(editingWorkflowIndex, field.uuid)"
                      />
                      <span class="attachment-name">{{ field.name }}</span>
                      <span class="attachment-model">({{ field.model }})</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div> <!-- closing job-config-full-container -->
          </div> <!-- closing workflow-content -->
          
          <div class="workflow-footer">
            <button @click="editingWorkflowIndex = null; showFieldsPanel = false;" class="btn-close-save">
              Close & Save
            </button>
          </div>
        </div> <!-- closing workflow-card -->

        <!-- Available Fields Panel (Only visible after example opportunity is picked) -->
        <div v-if="showFieldsPanel && selectedExampleOpportunity[editingWorkflowIndex]" class="available-fields-panel">
          <div class="fields-panel-header">
            <h4>Available Fields</h4>
            <button 
              @click="refreshAllFields" 
              class="btn-refresh-small" 
              :disabled="refreshingFields"
              title="Refresh fields from {{ brand.agencyName }}"
            >
              <RefreshCw :size="14" :class="{ 'loading': refreshingFields }" />
            </button>
          </div>
          <div class="fields-panel-content">
            <div class="fields-search">
              <input 
                type="text"
                v-model="fieldsSearchTerm"
                class="fields-search-input"
                placeholder="Search available fields..."
              />
            </div>
            <div class="fields-list">
              <div 
                v-for="section in availableFieldsSections" 
                :key="section.name"
                class="fields-section"
              >
                <h5 class="fields-section-title">
                  {{ section.name }} 
                  <span v-if="section.name === 'Opportunities' || section.name === 'Contacts'" style="color: #64748b; font-size: 10px; font-weight: normal;">
                    ({{ section.fields.length }} fields)
                  </span>
                </h5>
                <div class="fields-section-items">
                  <div
                    v-for="field in (section.fields || []).filter(f => f && f.key)"
                    :key="field.key"
                    class="field-bubble draggable-field field-with-tooltip white-field-bubble"
                    :class="{ 'hidden': fieldsSearchTerm && !field.displayName.toLowerCase().includes(fieldsSearchTerm.toLowerCase()) }"
                    :style="{ backgroundColor: '#ffffff', border: '1px solid #24acfe', color: '#24acfe', fontWeight: '600', padding: '2px 4px', borderRadius: '3px', fontSize: '10px', position: 'relative', lineHeight: '1.2' }"
                    :draggable="true"
                    @dragstart="handleFieldDragStart($event, field.key)"
                    @dragend="handleFieldDragEnd"
                    @mouseenter="(e) => showFieldTooltip(e, field.key)"
                    @mouseleave="showTooltip = null"
                    :data-field-key="field.key"
                    :data-field-display="field.displayName"
                    :data-tooltip="getFieldTooltip(field.key)"
                  >
                    {{ field.displayName }}
                  </div>
                </div>
              </div>
            </div>
            <!-- Tooltip teleported to body to escape container overflow - single tooltip for all fields -->
            <Teleport to="body">
              <span 
                v-if="showTooltip && getFieldTooltip(showTooltip)"
                class="tooltip-text tooltip-horizontal"
                :class="{ 'tooltip-left': tooltipPosition.isLeft }"
                :style="getTooltipStyle(showTooltip)"
              >{{ getFieldTooltip(showTooltip) }}</span>
            </Teleport>
          </div>
        </div>
      </div> <!-- end workflow-editor-container -->
    </div> <!-- end workflow-edit-section -->
  </div> <!-- end job-mapping-tab -->

  <!-- Checklist Item Settings Modal -->
  <div v-if="editingItem" class="modal-overlay" @click.self="closeItemSettings">
    <div class="modal-container">
      <div class="modal-header">
        <div class="modal-title-group">
          <h3>Checklist Item</h3>
          <p>Configure the settings for this checklist item.</p>
        </div>
        <button @click="closeItemSettings" class="modal-close">×</button>
      </div>
      
      <div class="modal-body">
        <div class="modal-field">
          <label>Name:</label>
          <input 
            type="text" 
            v-model="editingItem.item.name" 
            class="modal-input" 
            placeholder="Enter task name"
          />
        </div>
        
        <div class="modal-field">
          <label>Reminder:</label>
          <select v-model="editingItem.item.reminder" class="modal-select">
            <option value="none">No Reminder</option>
            <option value="CHECK_IN">Check-in (Job Start)</option>
            <option value="CHECK_OUT">Check-out (Job Finish)</option>
            <option value="NAVIGATE">Navigate (Driving)</option>
            <option value="RELATIVE_DATETIME">X Days (Time-based)</option>
          </select>
        </div>
        
        <div v-if="editingItem.item.reminder === 'RELATIVE_DATETIME'" class="modal-field">
          <label>Reminder Days:</label>
          <input 
            type="number" 
            v-model.number="editingItem.item.reminderDays" 
            class="modal-input" 
            min="1"
          />
        </div>
        
        <div class="modal-field">
          <label>Assigned To:</label>
          <select v-model="editingItem.item.assignedTo" class="modal-select">
            <option :value="null">No Assignment</option>
            <option v-for="member in staff" :key="member.uuid" :value="member.uuid">
              {{ member.first }} {{ member.last }}
            </option>
          </select>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="closeItemSettings" class="btn-modal-cancel">Cancel</button>
        <button @click="saveItemSettings" class="btn-modal-save">Save Changes</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RichTextFieldInput from './RichTextFieldInput.vue';
import { Trash, RefreshCw, Search, MoreVertical } from 'lucide-vue-next';
import { brandMixin } from '../brandMixin';

export default {
  name: 'JobMappingTab',
  mixins: [brandMixin],
  components: {
    RichTextFieldInput,
    Trash,
    RefreshCw,
    Search,
    MoreVertical
  },
  directives: {
    focus: {
      inserted: function (el) {
        el.focus();
      }
    }
  },
  data() {
    return {
      mappings: [],
      loading: false,
      saving: false,
      workflows: [],
      nextWorkflowId: 1,
      pipelines: [],
      loadingPipelines: false,
      categories: [],
      badges: [],
      queues: [],
      staff: [],
      availableCustomFields: [], // ServiceM8 custom fields for dropdown
      servicem8CustomFields: [], // ServiceM8 custom fields (from existing-custom-fields endpoint)
      customObjects: [], // GHL custom objects
      customFields: [], // GHL custom fields
      customValues: [], // GHL custom values
      loadingMetadata: false,
      editingWorkflowIndex: null, // Tracks which workflow is currently being edited
      editingItem: null, // Stores { workflowIndex, checklistIndex, itemIndex, item: { ...clone... } }
      newlyAddedItemId: null, // Tracks which item is currently being initially named
      editingChecklistId: null, // Tracks which checklist name is being edited
      editingItemId: null, // Tracks which checklist item name is being edited
      fieldsSearchTerm: '',
      workflowSearchTerm: '', // Search term for workflows table
      activeDropdownIndex: null, // Index of the workflow with open 3-dot menu
      activeChecklistDropdown: null, // Tracks which checklist has open 3-dot menu: { workflowIndex, checklistIndex }
      showTooltip: null, // Track which field's tooltip should be visible
      tooltipPosition: { top: 0, left: 0, isLeft: false }, // Position for tooltip
      // Example opportunity data per workflow index
      exampleOpportunities: {}, // { [workflowIndex]: [] }
      selectedExampleOpportunity: {}, // { [workflowIndex]: { id, name, ... } }
      loadingExampleOpportunities: {}, // { [workflowIndex]: boolean }
      loadingMoreOpportunities: {}, // { [workflowIndex]: boolean }
      exampleOpportunitiesHasMore: {}, // { [workflowIndex]: boolean }
      exampleOpportunitiesPage: {}, // { [workflowIndex]: number }
      exampleOpportunityData: {}, // { [workflowIndex]: { full opportunity data } }
      exampleOpportunitiesRefreshTimers: {}, // { [workflowIndex]: timerId } - for debouncing auto-refresh
      showingExamples: {}, // { [fieldId]: boolean } - tracks which fields are showing example preview
      activePreviewFieldId: null, // Tracks which field is currently being previewed (for global mouseup handler)
      refreshingFields: false, // Tracks if we are currently reloading fields from GHL
      saveTimer: null, // Debounce timer for saving workflows
      showFieldsPanel: false, // Explicitly control visibility of the available fields panel
      dragOverField: null, // Track which field is being dragged over
      availableFieldsSections: [
        { name: 'ServiceM8 formatted fields', fields: [
          { key: 'servicem8.client', displayName: 'Client' },
          { key: 'servicem8.jobAddress', displayName: 'Job Address' },
          { key: 'servicem8.mobile', displayName: 'Mobile' },
          { key: 'servicem8.phone', displayName: 'Phone' },
        ]},
        { name: 'Opportunities', fields: [] },
        { name: 'Contacts', fields: [] },
      ],
      config: {
        opportunityToJobEnabled: false,
        autoSyncOnUpdate: 'enabled',
      },
    }
  },
  watch: {
    'workflows': {
      deep: true,
      handler(newWorkflows, oldWorkflows) {
        // Watch for changes to trigger.stageId, trigger.status, or trigger.when
        newWorkflows.forEach((workflow, index) => {
          if (!workflow || !workflow.trigger) return;
          
          const trigger = workflow.trigger;
          const oldWorkflow = oldWorkflows && oldWorkflows[index];
          const oldTrigger = oldWorkflow && oldWorkflow.trigger;
          
          // Detect changes in trigger filters
          const stageChanged = trigger.stageId !== (oldTrigger && oldTrigger.stageId);
          const statusChanged = trigger.status !== (oldTrigger && oldTrigger.status);
          const whenChanged = trigger.when !== (oldTrigger && oldTrigger.when);
          const pipelineChanged = trigger.pipelineId !== (oldTrigger && oldTrigger.pipelineId);

          if (stageChanged || statusChanged || whenChanged || pipelineChanged) {
            console.log(`[Example] Trigger change detected for workflow ${index}`, {
              stageChanged, statusChanged, whenChanged, pipelineChanged
            });
            
            // Clear any existing timer for this workflow
            if (this.exampleOpportunitiesRefreshTimers[index]) {
              clearTimeout(this.exampleOpportunitiesRefreshTimers[index]);
            }
            
            // Debounce the refresh by 300ms to avoid excessive API calls when multiple dropdowns change
            this.exampleOpportunitiesRefreshTimers[index] = setTimeout(() => {
              if (this.shouldShowExampleOpportunityDropdown(index)) {
                console.log(`[Example] Auto-refreshing opportunities for workflow ${index}`);
                this.loadExampleOpportunities(index);
              } else {
                // Clear opportunities if dropdown should no longer be shown
                this.exampleOpportunities[index] = [];
                this.exampleOpportunityData[index] = null;
                this.selectedExampleOpportunity[index] = null;
              }
              // Clear the timer reference
              delete this.exampleOpportunitiesRefreshTimers[index];
            }, 300);
          }
        });
      }
    }
  },
  computed: {
    attachmentCapableFields() {
      return this.availableCustomFields.filter(f => 
        f.dataType === 'FILE_UPLOAD' || 
        f.dataType === 'file_upload' || 
        f.name?.toLowerCase().includes('attachment') ||
        f.type === 'FILE_UPLOAD'
      );
    }
  },
  created() {
    // Load cache immediately before component renders - this is synchronous and fast
    this.loadAllMetadataFromCache();
  },
  async mounted() {
    console.log('FIELD BUBBLES UPDATED - Compact styling with 1px 4px padding applied');
    // Load critical data first
    await this.loadMappings();
    await this.loadConfig();
    await this.loadWorkflows();
    await this.loadPipelines();
    
    // Ensure initial state is correctly set
    this.editingWorkflowIndex = null;
    this.showFieldsPanel = false;
    
    // Then refresh all metadata in background (cache already loaded in created())
    this.loadAllMetadataDeferred();
    
    // Add global mouseup listener to ensure preview is released even if mouse leaves button
    document.addEventListener('mouseup', this.handleGlobalMouseUp);
    document.addEventListener('touchend', this.handleGlobalMouseUp);
    window.addEventListener('click', this.handleGlobalClick);
  },
  beforeUnmount() {
    // Clean up any pending refresh timers
    Object.keys(this.exampleOpportunitiesRefreshTimers).forEach(index => {
      if (this.exampleOpportunitiesRefreshTimers[index]) {
        clearTimeout(this.exampleOpportunitiesRefreshTimers[index]);
      }
    });
    this.exampleOpportunitiesRefreshTimers = {};
    
    // Remove global event listeners
    document.removeEventListener('mouseup', this.handleGlobalMouseUp);
    document.removeEventListener('touchend', this.handleGlobalMouseUp);
    window.removeEventListener('click', this.handleGlobalClick);
  },
  methods: {
    handleGlobalClick(event) {
      // Close 3-dot menu if clicked outside
      if (this.activeDropdownIndex !== null) {
        const dropdown = this.$el.querySelector('.dropdown-container-active');
        if (dropdown && !dropdown.contains(event.target)) {
          this.activeDropdownIndex = null;
        }
      }
      // Close checklist dropdown if clicked outside
      if (this.activeChecklistDropdown !== null) {
        const dropdowns = this.$el.querySelectorAll('.dropdown-container-active');
        let clickedInside = false;
        dropdowns.forEach(dropdown => {
          if (dropdown.contains(event.target)) {
            clickedInside = true;
          }
        });
        if (!clickedInside) {
          this.activeChecklistDropdown = null;
        }
      }
    },
    toggleDropdown(index, event) {
      event.stopPropagation();
      if (this.activeDropdownIndex === index) {
        this.activeDropdownIndex = null;
      } else {
        this.activeDropdownIndex = index;
      }
    },
    toggleChecklistDropdown(workflowIndex, checklistIndex, event) {
      event.stopPropagation();
      if (this.activeChecklistDropdown && 
          this.activeChecklistDropdown.workflowIndex === workflowIndex && 
          this.activeChecklistDropdown.checklistIndex === checklistIndex) {
        this.activeChecklistDropdown = null;
      } else {
        this.activeChecklistDropdown = { workflowIndex, checklistIndex };
      }
    },
    async getSSOToken() {
      return await new Promise((resolve) => {
        window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");
        window.addEventListener("message", ({ data }) => {
          if (data.message === "REQUEST_USER_DATA_RESPONSE") {
            resolve(data.payload);
          }
        });
      });
    },
    async loadMappings() {
      this.loading = true;
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/mappings', {
          headers: {
            'x-sso-token': ssoToken,
          },
        });
        if (response.ok) {
          const data = await response.json();
          this.mappings = data.mappings || [];
        }
      } catch (error) {
        console.error('Error loading mappings:', error);
      } finally {
        this.loading = false;
      }
    },
    async loadConfig() {
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/config', {
          headers: {
            'x-sso-token': ssoToken,
          },
        });
        if (response.ok) {
          const data = await response.json();
          this.config = data.config || this.config;
        }
      } catch (error) {
        console.error('Error loading config:', error);
      }
    },
    async loadWorkflows() {
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/workflows', {
          method: 'GET',
          headers: {
            'x-sso-token': ssoToken,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const workflows = data.workflows || [];
          
          // Ensure all workflows have the complete structure
          this.workflows = workflows.map(workflow => {
            // Ensure jobConfig exists and has all required properties
            if (!workflow.jobConfig) {
              workflow.jobConfig = {};
            }
            
          // Initialize missing jobConfig properties with defaults
          const defaultJobConfig = {
            useSiteNameFormat: false,
            siteNameFormat: '',
            propertyOwner: '',
            company: '',
            poNumber: '',
            categoryUuid: '',
            badges: '',
            useCompanyBillingAddress: false,
            billingAddress: '',
            jobAddress: '',
            jobDescription: '',
            showJobContact: false,
            useStoredJobContact: false,
            selectedJobContact: '',
            jobContactFirstName: '',
            jobContactLastName: '',
            jobContactPhone: '',
            jobContactMobile: '',
            jobContactEmail: '',
            showPropertyManager: false,
            useStoredPropertyManager: false,
            selectedPropertyManager: '',
            propertyManagerFirstName: '',
            propertyManagerLastName: '',
            propertyManagerPhone: '',
            propertyManagerMobile: '',
            propertyManagerEmail: '',
            showBillingContact: false,
            useStoredBillingContact: false,
            selectedBillingContact: '',
            billingContactFirstName: '',
            billingContactLastName: '',
            billingContactPhone: '',
            billingContactMobile: '',
            billingContactEmail: '',
            showQueue: false,
            selectedQueue: null,
            queueExpiry: 7,
            queueRequiresAssignment: false,
            selectedStaff: null,
            checklists: [],
            notes: [],
            customFields: [],
            attachments: [],
            syncAllAttachments: true,
            autoSyncOnUpdate: true,
          };
          
          // Merge defaults with existing config
          workflow.jobConfig = { ...defaultJobConfig, ...workflow.jobConfig };

          // Explicitly remove deprecated/unwanted fields from the actual workflow object
          if (workflow.jobConfig.fieldMappings) delete workflow.jobConfig.fieldMappings;
          if (workflow.jobConfig.autoProcessToServiceM8 !== undefined) delete workflow.jobConfig.autoProcessToServiceM8;
          if (workflow.jobConfig.statusOverride === 'auto') delete workflow.jobConfig.statusOverride;
          if (workflow.jobConfig.defaultStatus) delete workflow.jobConfig.defaultStatus;
          
          // Ensure trigger exists
            if (!workflow.trigger) {
              workflow.trigger = {
                when: 'opportunity_stage_changed',
                pipelineId: '',
                status: 'all',
                stage: '',
                stageId: '',
                includeNew: false,
              };
        } else {
              // Ensure stageId exists in trigger
              if (workflow.trigger.stageId === undefined) {
                workflow.trigger.stageId = '';
              }
              // Ensure includeNew exists
              if (workflow.trigger.includeNew === undefined) {
                workflow.trigger.includeNew = false;
              }
            }
            
            // Ensure checklists exists and has proper structure
            if (!workflow.jobConfig.checklists || !Array.isArray(workflow.jobConfig.checklists)) {
              workflow.jobConfig.checklists = [];
            } else {
              // Ensure each checklist has proper structure
              workflow.jobConfig.checklists = workflow.jobConfig.checklists.map((checklist, idx) => {
                if (!checklist.id) {
                  checklist.id = `checklist-${Date.now()}-${idx}`;
                }
                if (!checklist.checklist_name) {
                  checklist.checklist_name = `Checklist ${idx + 1}`;
                }
                if (!checklist.items || !Array.isArray(checklist.items)) {
                  checklist.items = [];
                } else {
                  // Ensure each item has proper structure
                  checklist.items = checklist.items.map((item, itemIdx) => {
                    if (!item.id) {
                      item.id = `item-${Date.now()}-${itemIdx}`;
                    }
                    if (item.name === undefined) {
                      item.name = '';
                    }
                    if (item.reminder === undefined) {
                      item.reminder = 'none';
                    }
                    if (item.reminderDays === undefined) {
                      item.reminderDays = 1;
                    }
                    if (item.assignedTo === undefined) {
                      item.assignedTo = null;
                    }
                    if (item.completed === undefined) {
                      item.completed = false;
                    }
                    if (item.sortOrder === undefined) {
                      item.sortOrder = itemIdx;
                    }
                    return item;
                  });
                }
                if (checklist.sortOrder === undefined) {
                  checklist.sortOrder = idx;
                }
                return checklist;
              });
            }
            
            // Ensure notes exists and is an array of strings
            if (!workflow.jobConfig.notes || !Array.isArray(workflow.jobConfig.notes)) {
              workflow.jobConfig.notes = [];
            } else {
              // Ensure all notes are strings
              workflow.jobConfig.notes = workflow.jobConfig.notes.map(note => 
                typeof note === 'string' ? note : String(note || '')
              );
            }
            
            // Ensure enabled property exists
            if (workflow.enabled === undefined) {
              workflow.enabled = false;
            }
            
            return workflow;
          });
          
          // Update nextWorkflowId based on existing workflows
          if (this.workflows.length > 0) {
            const maxId = Math.max(...this.workflows.map(w => {
              const match = w.id?.match(/workflow-(\d+)/);
              return match ? parseInt(match[1]) : 0;
            }));
            this.nextWorkflowId = maxId + 1;
          }
          
          // Restore example opportunities for workflows that have exampleOpportunityId
          this.workflows.forEach(async (workflow, index) => {
            if (workflow.exampleOpportunityId && this.shouldShowExampleOpportunityDropdown(index)) {
              // Load opportunities first, then select the saved one
              await this.loadExampleOpportunities(index);
              // The loadExampleOpportunities will auto-select first, but we want the saved one
              if (this.exampleOpportunities[index]) {
                const savedOpp = this.exampleOpportunities[index].find(
                  opp => opp.id === workflow.exampleOpportunityId
                );
                if (savedOpp) {
                  this.onExampleOpportunitySelected(index, savedOpp.id);
                }
              }
            }
          });
        } else {
          // If endpoint doesn't exist, initialize with empty array
          this.workflows = [];
        }
      } catch (error) {
        console.error('Error loading workflows:', error);
        this.workflows = [];
      }
    },
    async saveWorkflows() {
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/workflows', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-sso-token': ssoToken,
          },
          body: JSON.stringify({ workflows: this.workflows }),
        });

        if (!response.ok) {
          console.error('Failed to save workflows');
        }
      } catch (error) {
        console.error('Error saving workflows:', error);
      }
    },
    saveWorkflow(index) {
      // Save individual workflow changes
      if (!this.workflows[index]) return;
      
      // Debounce saving to avoid excessive API calls and SSO token requests
      if (this.saveTimer) {
        clearTimeout(this.saveTimer);
      }
      
      this.saveTimer = setTimeout(async () => {
        await this.saveWorkflows();
        this.saveTimer = null;
      }, 500); // 500ms debounce
    },
    addNewWorkflow() {
      const newWorkflow = {
        id: `workflow-${this.nextWorkflowId++}`,
        name: `Workflow ${this.workflows.length + 1}`,
        enabled: false,
        trigger: {
          when: 'opportunity_stage_changed',
          pipelineId: '',
          status: '',
          stage: '',
          stageId: '',
          includeNew: false,
        },
        jobConfig: {
          useSiteNameFormat: false,
          siteNameFormat: '',
          propertyOwner: '',
          company: '',
          poNumber: '',
          categoryUuid: '',
          badges: '',
          useCompanyBillingAddress: false,
          billingAddress: '',
          jobAddress: '',
          jobDescription: '',
          showJobContact: false,
          useStoredJobContact: false,
          selectedJobContact: '',
          jobContactFirstName: '',
          jobContactLastName: '',
          jobContactPhone: '',
          jobContactMobile: '',
          jobContactEmail: '',
          showPropertyManager: false,
          useStoredPropertyManager: false,
          selectedPropertyManager: '',
          propertyManagerFirstName: '',
          propertyManagerLastName: '',
          propertyManagerPhone: '',
          propertyManagerMobile: '',
          propertyManagerEmail: '',
          showBillingContact: false,
          useStoredBillingContact: false,
          selectedBillingContact: '',
          billingContactFirstName: '',
          billingContactLastName: '',
          billingContactPhone: '',
          billingContactMobile: '',
          billingContactEmail: '',
          autoSyncOnUpdate: true,
          showQueue: false,
          selectedQueue: null,
          queueExpiry: 7,
          queueRequiresAssignment: false,
          selectedStaff: null,
          checklists: [],
          notes: [],
          customFields: [],
        },
      };
      this.workflows.push(newWorkflow);
      this.editingWorkflowIndex = this.workflows.length - 1;
      this.showFieldsPanel = true;
      this.saveWorkflows();
    },
    editWorkflow(index) {
      this.editingWorkflowIndex = index;
      this.showFieldsPanel = true;

      // Rebuild dynamic fields for the selected opportunity in this workflow
      if (this.exampleOpportunityData[index]) {
        this.rebuildDynamicAvailableFields({ opportunity: this.exampleOpportunityData[index] });
      }
    },
    deleteWorkflow(index) {
      if (!confirm(`Delete workflow "${this.workflows[index].name}"? This cannot be undone.`)) {
        return;
      }
      this.workflows.splice(index, 1);
      
      // Update editing index
      if (this.editingWorkflowIndex === index) {
        this.editingWorkflowIndex = null;
      } else if (this.editingWorkflowIndex > index) {
        this.editingWorkflowIndex--;
      }
      
      this.saveWorkflows();
    },
    async loadPipelines() {
      this.loadingPipelines = true;
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/pipelines', {
          method: 'GET',
          headers: {
            'x-sso-token': ssoToken,
          },
        });

        if (response.ok) {
          const data = await response.json();
          this.pipelines = data.pipelines || [];
          console.log(`Loaded ${this.pipelines.length} pipeline(s)`);
        } else {
          console.error('Failed to load pipelines:', response.statusText);
        }
      } catch (error) {
        console.error('Error loading pipelines:', error);
      } finally {
        this.loadingPipelines = false;
      }
    },
    async loadMetadata(background = false) {
      if (!background) {
        this.loadingMetadata = true;
      }
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        
        // Fetch Categories, Badges, Queues, Staff, and Custom Fields (via Job Probe) in parallel
        const [catRes, badgeRes, queueRes, staffRes, customFieldsRes] = await Promise.all([
          fetch('/servicem8/categories', { headers }),
          fetch('/servicem8/badges', { headers }),
          fetch('/servicem8/queues', { headers }),
          fetch('/servicem8/staff', { headers }),
          fetch('/servicem8/existing-custom-fields', { headers })
        ]);

        if (catRes.ok) {
          const data = await catRes.json();
          // Filter out inactive categories (active=0)
          this.categories = data.filter(c => c.active !== 0 && c.active !== '0');
          console.log(`Loaded ${this.categories.length} active category(s)`);
        }

        if (badgeRes.ok) {
          const data = await badgeRes.json();
          // Filter out inactive badges (active=0)
          this.badges = data.filter(b => b.active !== 0 && b.active !== '0');
          console.log(`Loaded ${this.badges.length} active badge(s)`);
        }

        if (queueRes.ok) {
          const data = await queueRes.json();
          // Filter out inactive queues (active=0)
          this.queues = data.filter(q => q.active !== 0 && q.active !== '0');
          console.log(`Loaded ${this.queues.length} active queue(s)`);
        }

        if (staffRes.ok) {
          const data = await staffRes.json();
          // Filter out inactive staff
          this.staff = data.filter(s => s.active !== 0 && s.active !== '0');
          console.log(`Loaded ${this.staff.length} active staff member(s)`);
        }

        if (customFieldsRes.ok) {
          const data = await customFieldsRes.json();
          // ServiceM8 custom fields are returned as an array
          this.servicem8CustomFields = Array.isArray(data) ? data : [];
          // Update availableCustomFields for dropdowns
          this.availableCustomFields = this.servicem8CustomFields.map(f => ({
            uuid: f.uuid || f.field_name,
            name: f.display_name || f.name || f.field_name,
            fieldName: f.field_name || f.uuid,
            model: 'job', // ServiceM8 custom fields are for jobs
            dataType: 'TEXT', // Default, ServiceM8 doesn't provide this
            type: 'TEXT',
            isNative: false,
            isHeuristic: f.isHeuristic || false
          }));
          console.log(`Loaded ${this.servicem8CustomFields.length} ServiceM8 custom field(s)`);
        }

        // Cache metadata for next time
        this.saveMetadataToCache();
      } catch (error) {
        console.error('Error loading ServiceM8 metadata:', error);
      } finally {
        this.loadingMetadata = false;
      }
    },
    async refreshAllFields() {
      this.refreshingFields = true;
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        
        // Fetch custom fields, objects, and values in parallel
        const [fieldsRes, objectsRes, valuesRes] = await Promise.all([
          fetch('/ghl/custom-fields', { headers }),
          fetch('/ghl/objects', { headers }),
          fetch('/ghl/custom-values', { headers })
        ]);

        if (fieldsRes.ok) {
          const data = await fieldsRes.json();
          this.customFields = data.customFields || data || [];
          this.updateAvailableFieldsWithCustomFields();
        }
        
        if (objectsRes.ok) {
          const data = await objectsRes.json();
          this.customObjects = data.objects || data || [];
          this.updateAvailableFieldsWithCustomObjects();
        }
        
        if (valuesRes.ok) {
          const data = await valuesRes.json();
          this.customValues = data.customValues || data || [];
          this.updateAvailableFieldsWithCustomValues();
        }
        
        // Cache GHL metadata
        this.saveGHLMetadataToCache();
        
        console.log('Successfully refreshed all fields from GHL');
      } catch (error) {
        console.error('Error refreshing fields from GHL:', error);
      } finally {
        this.refreshingFields = false;
      }
    },
    updateAvailableFieldsWithCustomFields() {
      // Group custom fields by model
      const models = [...new Set(this.customFields.map(f => f.model))];
      
      // Remove existing custom field sections
      this.availableFieldsSections = this.availableFieldsSections.filter(
        section => !section.name.endsWith(' Custom Fields')
      );
      
      models.forEach(model => {
        const fields = this.customFields.filter(f => f.model === model);
        if (fields.length > 0) {
          const sectionName = `${model.charAt(0).toUpperCase() + model.slice(1)} Custom Fields`;
          this.availableFieldsSections.push({
            name: sectionName,
            fields: fields.map(f => ({
              key: `${model}.${f.id}`,
              displayName: f.name
            }))
          });
        }
      });
      
      // Note: availableCustomFields is now populated from ServiceM8 custom fields
      // in loadMetadata(), not from GHL custom fields
    },
    updateAvailableFieldsWithCustomObjects() {
      // Capture existing dynamic fields before filtering
      const existingOppsFields = this.availableFieldsSections.find(s => s.name === 'Opportunities')?.fields || [];
      const existingContactsFields = this.availableFieldsSections.find(s => s.name === 'Contacts')?.fields || [];

      // Remove existing standard and custom object sections (but KEEP our new dynamic plural ones)
      const standardToReset = ['Company'];
      this.availableFieldsSections = this.availableFieldsSections.filter(
        section => !section.name.startsWith('Custom Object:') && !standardToReset.includes(section.name)
      );
      
      // Ensure we have standard objects even if not returned by GHL API
      const standardObjects = [
        { key: 'contact', labels: { singular: 'Contact' }, type: 'SYSTEM_DEFINED' },
        { key: 'opportunity', labels: { singular: 'Opportunity' }, type: 'SYSTEM_DEFINED' },
        { key: 'business', labels: { singular: 'Company' }, type: 'SYSTEM_DEFINED' }
      ];

      // Ensure Opportunities and Contacts sections exist (they are filtered in if already there, or added if missing)
      if (!this.availableFieldsSections.find(s => s.name === 'Opportunities')) {
        this.availableFieldsSections.push({ name: 'Opportunities', fields: existingOppsFields });
      }
      if (!this.availableFieldsSections.find(s => s.name === 'Contacts')) {
        this.availableFieldsSections.push({ name: 'Contacts', fields: existingContactsFields });
      }

      // Merge standard objects with custom ones, ensuring no duplicates by key
      const allObjects = [...standardObjects];
      this.customObjects.forEach(obj => {
        if (!allObjects.find(o => o.key === obj.key)) {
          allObjects.push(obj);
        }
      });

      // Add objects as new sections
      allObjects.forEach(obj => {
        const objectKey = obj.key; // e.g., "contact", "business", "custom_objects.pet"
        let sectionName = obj.labels?.singular || obj.labels?.plural || objectKey.split('.').pop();
        
        // Map system keys to standard names
        if (objectKey === 'contact') sectionName = 'Contact';
        if (objectKey === 'opportunity') sectionName = 'Opportunity';
        if (objectKey === 'business') sectionName = 'Company';

        const fields = [];
        
        // Custom handling for standard objects to ensure all useful fields are present
        if (objectKey === 'contact') {
          fields.push(
            { key: 'contact.name', displayName: 'Name' },
            { key: 'contact.companyName', displayName: 'Company Name' },
            { key: 'contact.email', displayName: 'Email' },
            { key: 'contact.phone', displayName: 'Phone' },
            { key: 'contact.address1', displayName: 'Address' },
            { key: 'contact.city', displayName: 'City' },
            { key: 'contact.state', displayName: 'State' },
            { key: 'contact.postalCode', displayName: 'Postcode' }
          );
        } else if (objectKey === 'opportunity') {
          fields.push(
            { key: 'opportunity.name', displayName: 'Name' },
            { key: 'opportunity.status', displayName: 'Status' },
            { key: 'opportunity.monetaryValue', displayName: 'Monetary Value' },
            { key: 'opportunity.pipelineId', displayName: 'Pipeline ID' },
            { key: 'opportunity.pipelineStageId', displayName: 'Stage ID' },
            { key: 'opportunity.source', displayName: 'Source' },
            { key: 'opportunity.tags', displayName: 'Tags' },
            { key: 'opportunity.assigned_to', displayName: 'Assigned To' },
            { key: 'opportunity.contact_id', displayName: 'Contact ID' },
            { key: 'opportunity.created_at', displayName: 'Created At' },
            { key: 'opportunity.updated_at', displayName: 'Updated At' }
          );
        } else if (objectKey === 'business') {
          fields.push(
            { key: 'company.name', displayName: 'Company Name' },
            { key: 'company.address', displayName: 'Address' },
            { key: 'company.city', displayName: 'City' },
            { key: 'company.state', displayName: 'State' },
            { key: 'company.postalCode', displayName: 'Postcode' },
            { key: 'company.phone', displayName: 'Phone' },
            { key: 'company.website', displayName: 'Website' }
          );
        }
        
        // ... (rest of the logic for custom objects) ...
        if (obj.addRecordConfiguration && Array.isArray(obj.addRecordConfiguration)) {
          obj.addRecordConfiguration.forEach(config => {
            if (config.key && !fields.find(f => f.key === config.key)) {
              const fieldKey = config.key;
              const displayName = fieldKey.split('.').pop()
                .replace(/_/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase());
              fields.push({
                key: fieldKey,
                displayName: displayName
              });
            }
          });
        }
        
        if (fields.length === 0) {
          if (obj.primaryDisplayProperty) {
            const displayKey = obj.primaryDisplayProperty.split('.').pop();
            fields.push({
              key: obj.primaryDisplayProperty,
              displayName: displayKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
            });
          }
          if (obj.searchableProperties) {
            obj.searchableProperties.forEach(prop => {
              if (fields.find(f => f.key === prop)) return;
              const propKey = prop.split('.').pop();
              fields.push({
                key: prop,
                displayName: propKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
              });
            });
          }
        }
        
        if (fields.length === 0) {
          fields.push({
            key: objectKey,
            displayName: sectionName
          });
        }

        const finalName = obj.type === 'SYSTEM_DEFINED' ? sectionName : `Custom Object: ${sectionName}`;
        this.availableFieldsSections.push({
          name: finalName,
          fields: fields
        });
      });
      
      // Sort sections: ServiceM8 formatted fields first, then standard ones, then Custom Objects
      const standardOrder = ['ServiceM8 formatted fields', 'Opportunities', 'Contacts', 'Company'];
      this.availableFieldsSections.sort((a, b) => {
        const indexA = standardOrder.indexOf(a.name);
        const indexB = standardOrder.indexOf(b.name);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.name.localeCompare(b.name);
      });
    },
    updateAvailableFieldsWithCustomValues() {
      // Remove existing custom value section
      this.availableFieldsSections = this.availableFieldsSections.filter(
        section => section.name !== 'Custom Values'
      );
      
      if (this.customValues.length > 0) {
        this.availableFieldsSections.push({
          name: 'Custom Values',
          fields: this.customValues.map(v => ({
            key: `custom_values.${v.name}`,
            displayName: v.name
          }))
        });
      }
    },
    async loadAllMetadataDeferred() {
      // Refresh background metadata silently
      this.loadMetadata(true);
      this.refreshAllFields();
    },
    saveMetadataToCache() {
      const cache = {
        categories: this.categories,
        badges: this.badges,
        queues: this.queues,
        staff: this.staff,
        servicem8CustomFields: this.servicem8CustomFields,
        availableCustomFields: this.availableCustomFields,
        timestamp: Date.now()
      };
      localStorage.setItem('servicem8_metadata_cache', JSON.stringify(cache));
    },
    saveGHLMetadataToCache() {
      const cache = {
        customFields: this.customFields,
        customObjects: this.customObjects,
        customValues: this.customValues,
        timestamp: Date.now()
      };
      localStorage.setItem('ghl_metadata_cache', JSON.stringify(cache));
    },
    loadAllMetadataFromCache() {
      try {
        const sm8CacheStr = localStorage.getItem('servicem8_metadata_cache');
        if (sm8CacheStr) {
          const cache = JSON.parse(sm8CacheStr);
          this.categories = cache.categories || [];
          this.badges = cache.badges || [];
          this.queues = cache.queues || [];
          this.staff = cache.staff || [];
          this.servicem8CustomFields = cache.servicem8CustomFields || [];
          this.availableCustomFields = cache.availableCustomFields || [];
        }
        
        const ghlCacheStr = localStorage.getItem('ghl_metadata_cache');
        if (ghlCacheStr) {
          const cache = JSON.parse(ghlCacheStr);
          this.customFields = cache.customFields || [];
          this.customObjects = cache.customObjects || [];
          this.customValues = cache.customValues || [];
          
          // Re-populate Available Fields sections from cache
          this.updateAvailableFieldsWithCustomFields();
          this.updateAvailableFieldsWithCustomObjects();
          this.updateAvailableFieldsWithCustomValues();
        }
      } catch (e) {
        console.error('Error loading metadata from cache:', e);
      }
    },
    handleTriggerChange(workflowIndex) {
      // Save the workflow first
      this.saveWorkflow(workflowIndex);
      
      // Clear selection when trigger changes to force user to pick a matching example
      this.selectedExampleOpportunity = { ...this.selectedExampleOpportunity, [workflowIndex]: null };
      this.exampleOpportunityData = { ...this.exampleOpportunityData, [workflowIndex]: null };
      
      // Clear any existing debounce timer
      if (this.exampleOpportunitiesRefreshTimers[workflowIndex]) {
        clearTimeout(this.exampleOpportunitiesRefreshTimers[workflowIndex]);
        delete this.exampleOpportunitiesRefreshTimers[workflowIndex];
      }
      
      // Immediately refresh opportunities if dropdown should be shown
      if (this.shouldShowExampleOpportunityDropdown(workflowIndex)) {
        console.log(`[Example] Trigger change - refreshing opportunities for workflow ${workflowIndex}`);
        this.loadExampleOpportunities(workflowIndex);
      } else {
        // Clear opportunities if dropdown should no longer be shown
        this.exampleOpportunities[workflowIndex] = [];
        this.exampleOpportunityData[workflowIndex] = null;
        this.selectedExampleOpportunity[workflowIndex] = null;
      }
    },
    handlePipelineChange(workflowIndex) {
      const workflow = this.workflows[workflowIndex];
      // Reset stage when pipeline changes
      workflow.trigger.stageId = '';
      
      // Trigger refresh and save after pipeline change
      this.handleTriggerChange(workflowIndex);
    },
    handleQueueChange(workflowIndex) {
      const workflow = this.workflows[workflowIndex];
      const selectedQueueUuid = workflow.jobConfig.selectedQueue;
      
      if (selectedQueueUuid) {
        // Find the selected queue in the queues array
        const selectedQueue = this.queues.find(q => q.uuid === selectedQueueUuid);
        
        if (selectedQueue) {
          // Update queue expiry to the queue's default_timeframe
          if (selectedQueue.default_timeframe !== undefined && selectedQueue.default_timeframe !== null) {
            workflow.jobConfig.queueExpiry = parseInt(selectedQueue.default_timeframe) || 7;
          }
          
          // Update queueRequiresAssignment based on the queue's requires_assignment
          if (selectedQueue.requires_assignment !== undefined) {
            workflow.jobConfig.queueRequiresAssignment = selectedQueue.requires_assignment === "1" || selectedQueue.requires_assignment === 1;
          }
        }
      } else {
        // If queue is cleared, reset to defaults
        workflow.jobConfig.queueExpiry = 7;
        workflow.jobConfig.queueRequiresAssignment = false;
        workflow.jobConfig.selectedStaff = null;
      }
      
      // Save the workflow after updating
      this.saveWorkflow(workflowIndex);
    },
    getPipelineStages(pipelineId) {
      const pipeline = this.pipelines.find(p => p.id === pipelineId);
      return pipeline ? pipeline.stages || [] : [];
    },
    isTriggerComplete(workflowIndex) {
      const workflow = this.workflows[workflowIndex];
      if (!workflow || !workflow.trigger) return false;
      
      if (workflow.trigger.when === 'opportunity_stage_changed') {
        return !!workflow.trigger.pipelineId && !!workflow.trigger.stageId;
      }
      
      if (workflow.trigger.when === 'opportunity_status_changed') {
        return !!workflow.trigger.status;
      }
      
      return false;
    },
    shouldShowExampleOpportunityDropdown(workflowIndex) {
      return this.isTriggerComplete(workflowIndex);
    },
    async loadExampleOpportunities(workflowIndex) {
      const workflow = this.workflows[workflowIndex];
      if (!this.shouldShowExampleOpportunityDropdown(workflowIndex)) return;

      // Set loading state
      if (!this.loadingExampleOpportunities[workflowIndex]) {
        this.loadingExampleOpportunities = { ...this.loadingExampleOpportunities, [workflowIndex]: true };
      }

      try {
        const ssoToken = await this.getSSOToken();
        const params = new URLSearchParams();
        if (workflow.trigger.pipelineId) params.append('pipeline_id', workflow.trigger.pipelineId);
        if (workflow.trigger.stageId) params.append('pipeline_stage_id', workflow.trigger.stageId);
        if (workflow.trigger.status) params.append('status', workflow.trigger.status);
        params.append('limit', '5');
        params.append('page', '1');
        
        const response = await fetch(`/ghl/example-opportunities?${params.toString()}`, {
          headers: { 'x-sso-token': ssoToken }
        });

        if (response.ok) {
          const data = await response.json();
          const opportunities = data.opportunities || [];
          this.exampleOpportunities = { ...this.exampleOpportunities, [workflowIndex]: opportunities };
          this.exampleOpportunitiesHasMore = { ...this.exampleOpportunitiesHasMore, [workflowIndex]: data.hasMore || false };
          this.exampleOpportunitiesPage = { ...this.exampleOpportunitiesPage, [workflowIndex]: 1 };

          // If we have a saved selection, trigger a rebuild from the data we just loaded
          const savedOppId = this.workflows[workflowIndex].exampleOpportunityId;
          if (savedOppId) {
            const savedOpp = opportunities.find(o => o.id === savedOppId);
            if (savedOpp) {
              this.rebuildDynamicAvailableFields({ opportunity: savedOpp });
            }
          }
        }
      } catch (error) {
        console.error('Error loading example opportunities:', error);
      } finally {
        this.loadingExampleOpportunities = { ...this.loadingExampleOpportunities, [workflowIndex]: false };
      }
    },
    async loadMoreExampleOpportunities(workflowIndex) {
      const workflow = this.workflows[workflowIndex];
      const currentPage = this.exampleOpportunitiesPage[workflowIndex] || 1;
      const nextPage = currentPage + 1;

      this.loadingMoreOpportunities = { ...this.loadingMoreOpportunities, [workflowIndex]: true };

      try {
        const ssoToken = await this.getSSOToken();
        const params = new URLSearchParams();
        if (workflow.trigger.pipelineId) params.append('pipeline_id', workflow.trigger.pipelineId);
        if (workflow.trigger.stageId) params.append('pipeline_stage_id', workflow.trigger.stageId);
        if (workflow.trigger.status) params.append('status', workflow.trigger.status);
        params.append('limit', '5');
        params.append('page', nextPage.toString());
        
        const response = await fetch(`/ghl/example-opportunities?${params.toString()}`, {
          headers: { 'x-sso-token': ssoToken }
        });

        if (response.ok) {
          const data = await response.json();
          const currentOpps = this.exampleOpportunities[workflowIndex] || [];
          this.exampleOpportunities = { 
            ...this.exampleOpportunities, 
            [workflowIndex]: [...currentOpps, ...(data.opportunities || [])] 
          };
          this.exampleOpportunitiesHasMore = { ...this.exampleOpportunitiesHasMore, [workflowIndex]: data.hasMore || false };
          this.exampleOpportunitiesPage = { ...this.exampleOpportunitiesPage, [workflowIndex]: nextPage };
        } else {
          // If we get an error (like 422 for no more pages), hide the button
          this.exampleOpportunitiesHasMore = { ...this.exampleOpportunitiesHasMore, [workflowIndex]: false };
        }
      } catch (error) {
        console.error('Error loading more example opportunities:', error);
        this.exampleOpportunitiesHasMore = { ...this.exampleOpportunitiesHasMore, [workflowIndex]: false };
      } finally {
        this.loadingMoreOpportunities = { ...this.loadingMoreOpportunities, [workflowIndex]: false };
      }
    },
    rebuildDynamicAvailableFields(fullData) {
      if (!fullData) {
        console.error('[Fields] rebuildDynamicAvailableFields called with no data!');
        return;
      }
      
      const opportunity = fullData.opportunity || fullData;
      const contact = opportunity.contact || fullData.contact;

      console.log('[Fields] ===== REBUILDING DYNAMIC FIELDS =====');
      console.log('[Fields] Opportunity keys:', Object.keys(opportunity));
      console.log('[Fields] Contact keys:', contact ? Object.keys(contact) : 'No contact');

      // Update Opportunities section
      const opportunityFields = Object.keys(opportunity)
        .filter(key => {
          const val = opportunity[key];
          const isPrimitive = typeof val !== 'object' || val === null;
          if (!isPrimitive) {
            console.log(`[Fields] Skipping ${key} (not primitive):`, typeof val);
          }
          return isPrimitive;
        })
        .map(key => {
          let displayName = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim();
          
          // Map IDs to Human Readable Names
          if (key === 'pipelineId') {
            const pipeline = this.pipelines.find(p => p.id === opportunity[key]);
            if (pipeline) {
              displayName = `Pipeline: ${pipeline.name}`;
              console.log(`[Fields] Mapped pipelineId to: ${displayName}`);
            }
          } else if (key === 'pipelineStageId') {
            const pipeline = this.pipelines.find(p => p.id === opportunity.pipelineId);
            const stage = pipeline?.stages?.find(s => s.id === opportunity[key]);
            if (stage) {
              displayName = `Stage: ${stage.name}`;
              console.log(`[Fields] Mapped pipelineStageId to: ${displayName}`);
            }
          } else if (key === 'monetaryValue') {
            displayName = 'Monetary Value';
          }
          
          return {
            key: `opportunity.${key}`,
            displayName: displayName
          };
        });
      
      console.log(`[Fields] Generated ${opportunityFields.length} opportunity fields:`, opportunityFields.map(f => f.displayName));
      
      const oppSectionIndex = this.availableFieldsSections.findIndex(s => s.name === 'Opportunities');
      console.log(`[Fields] Opportunities section index: ${oppSectionIndex}`);
      
      if (oppSectionIndex !== -1) {
        // Vue 3: Direct assignment should work, but use splice for guaranteed reactivity
        // Filter out any invalid fields before updating
        const validFields = opportunityFields.filter(f => f && f.key && f.displayName);
        this.availableFieldsSections.splice(oppSectionIndex, 1, {
          name: 'Opportunities',
          fields: validFields
        });
        console.log(`[Fields] Updated Opportunities section with ${validFields.length} fields`);
      } else {
        const validFields = opportunityFields.filter(f => f && f.key && f.displayName);
        this.availableFieldsSections.push({ name: 'Opportunities', fields: validFields });
        console.log(`[Fields] Added new Opportunities section with ${validFields.length} fields`);
      }

      // Update Contacts section
      if (contact) {
        const contactFields = Object.keys(contact)
          .filter(key => typeof contact[key] !== 'object' || contact[key] === null)
          .map(key => ({
            key: `contact.${key}`,
            displayName: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim()
          }));
        
        console.log(`[Fields] Generated ${contactFields.length} contact fields:`, contactFields.map(f => f.displayName));
        
        const contactSectionIndex = this.availableFieldsSections.findIndex(s => s.name === 'Contacts');
        console.log(`[Fields] Contacts section index: ${contactSectionIndex}`);
        
        if (contactSectionIndex !== -1) {
          // Filter out any invalid fields before updating
          const validFields = contactFields.filter(f => f && f.key && f.displayName);
          this.availableFieldsSections.splice(contactSectionIndex, 1, {
            name: 'Contacts',
            fields: validFields
          });
          console.log(`[Fields] Updated Contacts section with ${validFields.length} fields`);
        } else {
          const validFields = contactFields.filter(f => f && f.key && f.displayName);
          this.availableFieldsSections.push({ name: 'Contacts', fields: validFields });
          console.log(`[Fields] Added new Contacts section with ${validFields.length} fields`);
        }
      }

      // Re-sort to ensure ServiceM8 formatted fields are at the top, then Opportunities and Contacts
      const standardOrder = ['ServiceM8 formatted fields', 'Opportunities', 'Contacts', 'Company'];
      this.availableFieldsSections.sort((a, b) => {
        const indexA = standardOrder.indexOf(a.name);
        const indexB = standardOrder.indexOf(b.name);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.name.localeCompare(b.name);
      });
      
      console.log('[Fields] Final sections:', this.availableFieldsSections.map(s => ({ name: s.name, fieldCount: s.fields.length })));
      
      // Force Vue to re-render
      this.$forceUpdate();
      console.log('[Fields] ===== REBUILD COMPLETE =====');
    },
    async onExampleOpportunitySelected(workflowIndex, opportunityId) {
      if (!opportunityId) {
        this.selectedExampleOpportunity = { ...this.selectedExampleOpportunity, [workflowIndex]: null };
        this.exampleOpportunityData = { ...this.exampleOpportunityData, [workflowIndex]: null };
        return;
      }

      const opp = (this.exampleOpportunities[workflowIndex] || []).find(o => o.id === opportunityId);
      this.selectedExampleOpportunity = { ...this.selectedExampleOpportunity, [workflowIndex]: opp };
      
      // IMMEDIATELY populate fields from what we already have from the first call (search results)
      if (opp) {
        this.rebuildDynamicAvailableFields({ opportunity: opp });
      }
      
      // Save selection to workflow
      this.workflows[workflowIndex].exampleOpportunityId = opportunityId;
      this.saveWorkflow(workflowIndex);

      // Fetch full opportunity and contact details for preview
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch(`/ghl/opportunities/${opportunityId}`, {
          headers: { 'x-sso-token': ssoToken }
        });

        if (response.ok) {
          const fullOpp = await response.json();
          
          // The response might be { opportunity: { ... } } as per user query
          const opportunityData = fullOpp.opportunity || fullOpp;
          
          // Fetch full contact details - always fetch to ensure we have customFields
          let fullContact = opportunityData.contact || null;
          const contactId = opportunityData.contactId || (opportunityData.contact ? opportunityData.contact.id : null);
          
          // Always fetch full contact to ensure customFields are included
          if (contactId) {
            const contactRes = await fetch(`/ghl/contact/${contactId}`, {
              headers: { 'x-sso-token': ssoToken }
            });
            if (contactRes.ok) {
              const contactResponse = await contactRes.json();
              // Handle both wrapped { contact: {...} } and unwrapped {...} responses
              const fetchedContact = contactResponse.contact || contactResponse;
              // Merge with existing contact data, prioritizing fetched data
              fullContact = {
                ...(fullContact || {}),
                ...fetchedContact,
                // Ensure customFields are included from fetched data
                customFields: fetchedContact.customFields || (fullContact && fullContact.customFields) || []
              };
            } else {
              console.warn(`[Preview] Failed to fetch contact ${contactId}, status:`, contactRes.status);
            }
          }

          const combinedData = {
            ...opportunityData,
            contact: fullContact
          };

          this.exampleOpportunityData = { 
            ...this.exampleOpportunityData, 
            [workflowIndex]: combinedData
          };
          
          // Rebuild the dynamic fields sections again with the FULL data
          this.rebuildDynamicAvailableFields({ opportunity: combinedData });
          
        }
      } catch (error) {
        console.error('Error loading full opportunity data:', error);
      }
    },
    getFieldExampleValue(workflowIndex, fieldKey) {
      const data = this.exampleOpportunityData[workflowIndex];
      if (!data) {
        return null;
      }

      const parts = fieldKey.split('.');
      
      // Helper to extract filename from URL or array of URLs
      const formatValue = (val) => {
        if (!val) return val;
        if (Array.isArray(val)) {
          return val.map(v => formatValue(v)).join(', ');
        }
        if (typeof val === 'string' && val.startsWith('http')) {
          try {
            const url = new URL(val);
            return url.pathname.split('/').pop() || val;
          } catch (e) {
            return val;
          }
        }
        return val;
      };

      // Handle custom field mapping FIRST (before standard field checks)
      // Custom fields have keys like "opportunity.{customFieldId}" or "contact.{customFieldId}"
      if (parts[0] === 'opportunity' && parts.length === 2 && parts[1]) {
        const fieldKey = parts[1];
        // Get opportunity data (might be nested or at top level)
        // The data structure can be either:
        // 1. { opportunity: {...}, contact: {...} } - nested
        // 2. { ...opportunityData, contact: {...} } - spread at top level
        const oppData = data.opportunity || data;
        
        // ALWAYS check custom fields first for opportunity fields
        const oppCustomFields = oppData.customFields || [];
        if (oppCustomFields.length > 0) {
          const customField = oppCustomFields.find(f => f.id === fieldKey);
          if (customField) {
            // Handle both value and fieldValue properties, and array fieldValue
            if (Array.isArray(customField.fieldValue)) {
              return formatValue(customField.fieldValue.map(function(f) { return (f.meta && f.meta.name) || f.url || 'File'; }).join(', '));
            }
            return formatValue(customField.value || customField.fieldValue);
          }
        }
        
        // If not a custom field, try standard field access
        // Check both the opportunity object (if nested) and top-level data
        let val = oppData[fieldKey];
        // If oppData is data.opportunity (nested), we already have the right value
        // If oppData is data (spread), we also have the right value
        // But if val is undefined and we're using nested structure, also check top level
        if (val === undefined && data.opportunity && data !== oppData) {
          val = data[fieldKey];
        }
        // Also check if the field is at the top level when data structure is spread
        if (val === undefined && !data.opportunity) {
          val = data[fieldKey];
        }
        
        if (val !== undefined) {
          // Handle null - return null (not empty string)
          if (val === null) {
            return null;
          }
          // If it's a Date object, format it
          if (val instanceof Date) {
            return formatValue(val.toISOString());
          }
          // If it's a string, number, boolean, or array, return it
          if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean' || Array.isArray(val)) {
            return formatValue(val);
        }
          // If it's a plain object (not Date), skip it
          if (typeof val === 'object') {
            return null;
          }
          return formatValue(val);
        }
      } else if (parts[0] === 'contact' && parts.length === 2 && parts[1]) {
        const fieldKey = parts[1];
        // Get contact data (might be nested in opportunity or at top level)
        const contactData = data.contact || (data.opportunity && data.opportunity.contact) || null;
        
        if (contactData) {
          // ALWAYS check custom fields first for contact fields
          const contactCustomFields = contactData.customFields || [];
          if (contactCustomFields.length > 0) {
            const customField = contactCustomFields.find(function(f) { return f.id === fieldKey; });
            if (customField) {
              // Handle both value and fieldValue properties, and array fieldValue
              if (Array.isArray(customField.fieldValue)) {
                return formatValue(customField.fieldValue.map(function(f) { return f.meta && f.meta.name || f.url || 'File'; }).join(', '));
              }
              return formatValue(customField.value || customField.fieldValue);
            }
          }
          
          // If not a custom field, try standard field access (but skip if it's an object/array)
          const val = contactData[fieldKey];
          if (val !== undefined && typeof val !== 'object') {
            return formatValue(val);
          }
        }
      } else if (parts[0] === 'company') {
        if (parts[1] === 'name') return data.contact?.companyName || null;
        if (parts[1] === 'address') return data.contact?.address1 || null;
      } else if (parts[0] === 'servicem8') {
        // Handle ServiceM8 formatted fields
        if (parts[1] === 'client') {
          const contactData = data.contact || (data.opportunity && data.opportunity.contact) || null;
          if (contactData) {
            // If companyName is not blank, use it; otherwise use firstName + lastName
            const companyName = contactData.companyName;
            if (companyName && String(companyName).trim() !== '') {
              return formatValue(companyName);
            } else {
              // Concatenate firstName and lastName
              const firstName = contactData.firstName || '';
              const lastName = contactData.lastName || '';
              const fullName = `${firstName} ${lastName}`.trim();
              return fullName || null;
            }
          }
          return null;
        } else if (parts[1] === 'jobAddress') {
          const contactData = data.contact || (data.opportunity && data.opportunity.contact) || null;
          if (contactData) {
            const companyName = contactData.companyName;
            let addressParts = [];
            
            if (companyName && String(companyName).trim() !== '') {
              // Use company address fields
              const address1 = contactData.company?.address1 || contactData.address1 || '';
              const city = contactData.company?.city || contactData.city || '';
              const state = contactData.company?.state || contactData.state || '';
              const postcode = contactData.company?.postcode || contactData.postcode || contactData.postalCode || '';
              
              if (address1) addressParts.push(address1);
              const cityStatePostcode = [city, state, postcode].filter(v => v).join(' ');
              if (cityStatePostcode) addressParts.push(cityStatePostcode);
            } else {
              // Use contact address fields
              const address1 = contactData.address1 || '';
              const city = contactData.city || '';
              const state = contactData.state || '';
              const postcode = contactData.postcode || contactData.postalCode || '';
              
              if (address1) addressParts.push(address1);
              const cityStatePostcode = [city, state, postcode].filter(v => v).join(' ');
              if (cityStatePostcode) addressParts.push(cityStatePostcode);
            }
            
            // Join with newline
            return addressParts.length > 0 ? addressParts.join('\n') : null;
          }
          return null;
        } else if (parts[1] === 'mobile') {
          return this.formatPhoneNumber(this.getMobileNumber(data));
        } else if (parts[1] === 'phone') {
          return this.formatPhoneNumber(this.getPhoneNumber(data));
        }
      } else if (parts[0] === 'custom_values') {
        const valName = parts.slice(1).join('.');
        const customVal = this.customValues.find(v => v.name === valName);
        return customVal ? formatValue(customVal.value) : null;
      }

      // Check if this looks like a custom field ID (long alphanumeric string)
      // If so, don't do standard traversal - it will just return the ID
      const looksLikeCustomFieldId = parts.length === 2 && 
        (parts[0] === 'opportunity' || parts[0] === 'contact') && 
        parts[1] && 
        parts[1].length > 10 && 
        /^[a-zA-Z0-9]+$/.test(parts[1]);
      
      if (looksLikeCustomFieldId) {
        // This is likely a custom field ID that wasn't found - return null instead of trying standard traversal
        return null;
      }

      // Standard traversal (only for non-custom-field keys)
      let value = data;
      for (const part of parts) {
        if (value && typeof value === 'object') {
          value = value[part];
        } else {
          return null;
        }
      }
      
      return value !== undefined ? formatValue(value) : null;
    },
    toggleExamplePreview(fieldId, force = null) {
      if (!this.exampleOpportunityData[this.editingWorkflowIndex]) {
        console.warn('Cannot preview without an example opportunity selected');
        return;
      }
      
      // If force is null (click event), toggle the state
      // If force is true/false (mousedown/mouseup), use that value
      const newState = force !== null ? force : !this.showingExamples[fieldId];
      
      // If we're handling a group preview
      if (['job-contact', 'property-manager', 'billing-contact'].includes(fieldId)) {
        const updates = { [fieldId]: newState };
        
        // Map group names to actual field ID prefixes
        const fieldPrefixMap = {
          'job-contact': 'job-contact',
          'property-manager': 'pm',
          'billing-contact': 'billing-contact'
        };
        
        const prefix = fieldPrefixMap[fieldId];
        const suffixes = ['first-name', 'last-name', 'phone', 'mobile', 'email'];
        suffixes.forEach(suffix => {
          updates[`${prefix}-${suffix}-${this.editingWorkflowIndex}`] = newState;
        });
        
        this.showingExamples = { ...this.showingExamples, ...updates };
      } else {
        this.showingExamples = { ...this.showingExamples, [fieldId]: newState };
      }
      
    },
    showExamplePreview(fieldId, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.showingExamples = { ...this.showingExamples, [fieldId]: true };
      this.activePreviewFieldId = fieldId;
    },
    hideExamplePreview(fieldId, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.showingExamples = { ...this.showingExamples, [fieldId]: false };
      if (this.activePreviewFieldId === fieldId) {
        this.activePreviewFieldId = null;
      }
    },
    handleGlobalMouseUp() {
      // If we have an active preview, turn it off
      if (this.activePreviewFieldId) {
        this.showingExamples = { ...this.showingExamples, [this.activePreviewFieldId]: false };
        this.activePreviewFieldId = null;
      }
    },
    showFieldTooltip(event, fieldKey) {
      this.showTooltip = fieldKey;
      const button = event.currentTarget;
      const buttonRect = button.getBoundingClientRect();
      
      // Find the container (fields-list)
      const container = button.closest('.fields-list');
      if (!container) {
        // Fallback if container not found
        this.tooltipPosition = {
          top: buttonRect.top + (buttonRect.height / 2),
          left: buttonRect.right + 8,
          isLeft: false
        };
        return;
      }
      
      const containerRect = container.getBoundingClientRect();
      const tooltipWidth = 250; // Estimated max width
      
      // Calculate space available in container (viewport coordinates)
      const spaceOnRight = containerRect.right - buttonRect.right;
      const spaceOnLeft = buttonRect.left - containerRect.left;
      
      // Calculate tooltip position in viewport coordinates
      let tooltipLeft;
      let isLeft = false;
      
      if (spaceOnRight >= tooltipWidth + 16) {
        // Position to the right of button, within container bounds
        tooltipLeft = buttonRect.right + 8;
        // Ensure it doesn't exceed container right edge
        if (tooltipLeft + tooltipWidth > containerRect.right) {
          tooltipLeft = containerRect.right - tooltipWidth - 8;
        }
      } else if (spaceOnLeft >= tooltipWidth + 16) {
        // Position to the left of button, within container bounds
        tooltipLeft = buttonRect.left - tooltipWidth - 8;
        isLeft = true;
        // Ensure it doesn't exceed container left edge
        if (tooltipLeft < containerRect.left) {
          tooltipLeft = containerRect.left + 8;
          isLeft = false;
        }
      } else {
        // Not enough space on either side, default to right but clamp to container
        tooltipLeft = buttonRect.right + 8;
        if (tooltipLeft + tooltipWidth > containerRect.right) {
          tooltipLeft = containerRect.right - tooltipWidth - 8;
          if (tooltipLeft < containerRect.left) {
            tooltipLeft = containerRect.left + 8;
          }
        }
      }
      
      this.tooltipPosition = {
        top: buttonRect.top + (buttonRect.height / 2),
        left: tooltipLeft,
        isLeft: isLeft
      };
    },
    getFieldTooltip(fieldKey) {
      // First try to get example value if we have a workflow being edited
      if (this.editingWorkflowIndex !== null) {
        const exampleValue = this.getFieldExampleValue(this.editingWorkflowIndex, fieldKey);
        if (exampleValue !== null && exampleValue !== undefined && exampleValue !== '') {
          let tooltip = String(exampleValue);
          // Add explanatory note for ServiceM8 formatted fields
          if (fieldKey === 'servicem8.client') {
            tooltip += '\n\nUses company name if attached to a company, otherwise uses first name and last name.';
          } else if (fieldKey === 'servicem8.jobAddress') {
            tooltip += '\n\nUses company address if attached to a company, otherwise uses contact address.';
          } else if (fieldKey === 'servicem8.mobile') {
            tooltip += '\n\nFormats mobile numbers from international format to Australian local format (e.g., +61403704197 → 0403 704 197).';
          } else if (fieldKey === 'servicem8.phone') {
            tooltip += '\n\nFormats landline numbers from international format to Australian local format (e.g., +61738034779 → 07 3803 4779).';
          }
          return tooltip;
        }
      }

      // Find field definition to get its ID/Internal key for tooltip
      let fieldDef = null;
      for (const section of this.availableFieldsSections) {
        const found = section.fields.find(f => f.key === fieldKey);
        if (found) {
          fieldDef = found;
          break;
        }
      }
      
      if (!fieldDef) return null;
      
      // Add explanatory note for ServiceM8 formatted fields even when no example value
      if (fieldKey === 'servicem8.client') {
        return 'Uses company name if attached to a company, otherwise uses first name and last name.';
      } else if (fieldKey === 'servicem8.jobAddress') {
        return 'Uses company address if attached to a company, otherwise uses contact address.';
      } else if (fieldKey === 'servicem8.mobile') {
        return 'Formats mobile numbers from international format to Australian local format (e.g., +61403704197 → 0403 704 197).';
      } else if (fieldKey === 'servicem8.phone') {
        return 'Formats landline numbers from international format to Australian local format (e.g., +61738034779 → 07 3803 4779).';
      }
      
      // Use the key part after the object prefix
      const parts = fieldKey.split('.');
      const internalKey = parts.length > 1 ? parts.slice(1).join('.') : parts[0];
      
      // If it's a UUID (GHL custom field), don't show internal key unless no example value
      if (internalKey.length > 20 && /^[a-f0-9-]+$/i.test(internalKey)) {
        return null;
      }
      
      return internalKey;
    },
    getTooltipStyle(fieldKey) {
      const tooltipText = this.getFieldTooltip(fieldKey);
      const isMultiLine = (fieldKey === 'servicem8.client' || fieldKey === 'servicem8.jobAddress') || 
                         (tooltipText && tooltipText.includes && tooltipText.includes('\n'));
      
      return {
        position: 'fixed',
        left: this.tooltipPosition.left + 'px',
        top: this.tooltipPosition.top + 'px',
        transform: 'translateY(-50%)',
        whiteSpace: isMultiLine ? 'pre-wrap' : 'nowrap',
        background: '#4b5563',
        color: 'white',
        padding: '6px 10px',
        borderRadius: '6px',
        zIndex: 99999,
        pointerEvents: 'none',
        opacity: 1,
        transition: 'opacity 0.2s',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        maxWidth: isMultiLine ? '300px' : 'none'
      };
    },
    addChecklistToWorkflow(workflowIndex) {
      if (!this.workflows[workflowIndex].jobConfig.checklists) {
        this.workflows[workflowIndex].jobConfig.checklists = [];
      }
      
      const idx = this.workflows[workflowIndex].jobConfig.checklists.length;
      const newChecklist = {
        id: `checklist-${Date.now()}-${idx}`,
        checklist_name: `Checklist ${idx + 1}`,
        items: [],
        sortOrder: idx
      };
      
      this.workflows[workflowIndex].jobConfig.checklists.push(newChecklist);
      this.saveWorkflow(workflowIndex);
      
      // Immediately start editing the name
      this.startChecklistNameEdit(newChecklist.id);
    },
    removeChecklistFromWorkflow(workflowIndex, checklistIndex) {
      if (!confirm('Delete this checklist and all its tasks?')) return;
      
      this.workflows[workflowIndex].jobConfig.checklists.splice(checklistIndex, 1);
      this.saveWorkflow(workflowIndex);
    },
    addChecklistItemToWorkflow(workflowIndex, checklistIndex) {
      const checklist = this.workflows[workflowIndex].jobConfig.checklists[checklistIndex];
      const itemIdx = checklist.items.length;
      const newItem = {
        id: `item-${Date.now()}-${itemIdx}`,
        name: '',
        completed: false,
        reminder: 'none',
        reminderDays: 1,
        assignedTo: null,
        sortOrder: itemIdx
      };
      
      checklist.items.push(newItem);
      this.newlyAddedItemId = newItem.id;
      this.saveWorkflow(workflowIndex);
    },
    startChecklistNameEdit(checklistId) {
      // Clear other edits
      this.editingItemId = null;
      this.newlyAddedItemId = null;
      this.editingChecklistId = checklistId;
    },
    finishChecklistNameEdit(workflowIndex) {
      this.editingChecklistId = null;
      this.saveWorkflow(workflowIndex);
    },
    startItemNameEdit(itemId) {
      // Clear other edits
      this.editingChecklistId = null;
      this.newlyAddedItemId = null;
      this.editingItemId = itemId;
    },
    finishItemNameEdit(workflowIndex) {
      this.newlyAddedItemId = null;
      this.editingItemId = null;
      this.saveWorkflow(workflowIndex);
    },
    removeChecklistItemFromWorkflow(workflowIndex, checklistIndex, itemIndex) {
      const checklist = this.workflows[workflowIndex].jobConfig.checklists[checklistIndex];
      if (!checklist || !checklist.items || !checklist.items[itemIndex]) return;
      
      checklist.items.splice(itemIndex, 1);
      
      // Close settings if it was open for this item
      if (this.editingItem && 
          this.editingItem.workflowIndex === workflowIndex && 
          this.editingItem.checklistIndex === checklistIndex && 
          this.editingItem.itemIndex === itemIndex) {
        this.editingItem = null;
      }
      
      this.saveWorkflow(workflowIndex);
    },
    openItemSettings(workflowIndex, checklistIndex, itemIndex) {
      const item = this.workflows[workflowIndex].jobConfig.checklists[checklistIndex].items[itemIndex];
      this.editingItem = {
        workflowIndex,
        checklistIndex,
        itemIndex,
        item: JSON.parse(JSON.stringify(item)) // Deep clone to allow cancel
      };
    },
    closeItemSettings() {
      this.editingItem = null;
    },
    saveItemSettings() {
      if (!this.editingItem) return;
      
      const { workflowIndex, checklistIndex, itemIndex, item } = this.editingItem;
      // Update the real item with the changes from the modal
      this.workflows[workflowIndex].jobConfig.checklists[checklistIndex].items[itemIndex] = item;
      
      this.saveWorkflow(workflowIndex);
      this.closeItemSettings();
    },
    /**
     * Format Australian phone number from international format to local format
     * Mobile: +61403704197 -> 0403 704 197 (0XXX XXX XXX)
     * Landline: +61738034779 -> 07 3803 4779 (0X XXXX XXXX)
     * If number starts with +614, always format as mobile
     */
    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber || typeof phoneNumber !== 'string') return null;
      
      // Remove all non-digit characters except +
      let cleaned = phoneNumber.trim();
      
      // Check if it's a mobile number (starts with +614)
      const isMobileNumber = cleaned.startsWith('+614');
      
      // If it's a mobile number, format as mobile regardless of isMobile parameter
      if (isMobileNumber) {
        // Remove +61 and add 0
        let digits = cleaned.replace(/^\+61/, '').replace(/\D/g, '');
        if (digits.length === 9) {
          // Format as 0XXX XXX XXX
          return `${digits[0]}${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
        }
      } else if (cleaned.startsWith('+617')) {
        // Landline number
        let digits = cleaned.replace(/^\+61/, '').replace(/\D/g, '');
        if (digits.length === 9) {
          // Format as 0X XXXX XXXX
          return `${digits[0]}${digits[1]} ${digits.slice(2, 6)} ${digits.slice(6)}`;
        }
      }
      
      // Fallback: return cleaned number if format doesn't match
      return cleaned.replace(/^\+61/, '0');
    },
    /**
     * Get mobile number from contact data
     * Priority: 1. phoneLabel === "Mobile" in contact, 2. First +614 number, 3. null
     */
    getMobileNumber(data) {
      const contact = data.contact || (data.opportunity && data.opportunity.contact) || null;
      if (!contact) return null;
      
      // First priority: Check main contact phone if phoneLabel is "Mobile"
      if (contact.phone && contact.phoneLabel === 'Mobile') {
        return contact.phone;
      }
      
      // Second priority: Check additionalPhones for Mobile
      if (contact.additionalPhones && Array.isArray(contact.additionalPhones)) {
        const mobilePhone = contact.additionalPhones.find(p => p.phoneLabel === 'Mobile');
        if (mobilePhone && mobilePhone.phone) {
          return mobilePhone.phone;
        }
      }
      
      // Fallback: Check if main phone starts with +614 (mobile format)
      if (contact.phone && contact.phone.startsWith('+614')) {
        return contact.phone;
      }
      
      // Fallback: Check additionalPhones for any +614 number
      if (contact.additionalPhones && Array.isArray(contact.additionalPhones)) {
        const mobileFormatPhone = contact.additionalPhones.find(p => p.phone && p.phone.startsWith('+614'));
        if (mobileFormatPhone && mobileFormatPhone.phone) {
          return mobileFormatPhone.phone;
        }
      }
      
      return null;
    },
    /**
     * Get phone number (Home/Work/Landline) from contact data
     * Priority: 1. First Home/Work/Landline in additionalPhones, 2. Main phone if not Mobile, 3. null
     */
    getPhoneNumber(data) {
      const contact = data.contact || (data.opportunity && data.opportunity.contact) || null;
      if (!contact) return null;
      
      const phoneLabels = ['Home', 'Work', 'Landline'];
      
      // First priority: Check additionalPhones for Home/Work/Landline
      if (contact.additionalPhones && Array.isArray(contact.additionalPhones)) {
        const phoneNumber = contact.additionalPhones.find(p => 
          p.phone && phoneLabels.includes(p.phoneLabel)
        );
        if (phoneNumber && phoneNumber.phone) {
          return phoneNumber.phone;
        }
      }
      
      // Second priority: Check main contact phone if it's not Mobile
      if (contact.phone && contact.phoneLabel && phoneLabels.includes(contact.phoneLabel)) {
        return contact.phone;
      }
      
      // Fallback: If main phone is not Mobile and no Home/Work/Landline found, use main phone
      if (contact.phone && contact.phoneLabel !== 'Mobile') {
        return contact.phone;
      }
      
      return null;
    },
    addNoteToWorkflow(workflowIndex) {
      if (!this.workflows[workflowIndex].jobConfig.notes) {
        this.workflows[workflowIndex].jobConfig.notes = [];
      }
      this.workflows[workflowIndex].jobConfig.notes.push('');
      this.saveWorkflow(workflowIndex);
    },
    removeNoteFromWorkflow(workflowIndex, noteIndex) {
      if (this.workflows[workflowIndex].jobConfig.notes && 
          this.workflows[workflowIndex].jobConfig.notes[noteIndex] !== undefined) {
        this.workflows[workflowIndex].jobConfig.notes.splice(noteIndex, 1);
        this.saveWorkflow(workflowIndex);
      }
    },
    addCustomFieldToWorkflow(workflowIndex) {
      if (!this.workflows[workflowIndex].jobConfig.customFields) {
        this.workflows[workflowIndex].jobConfig.customFields = [];
      }
      this.workflows[workflowIndex].jobConfig.customFields.push({
        id: `cf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        fieldUuid: '',
        name: '',
        value: '',
        isNative: true,
        fieldType: 'Text'
      });
      this.saveWorkflow(workflowIndex);
    },
    removeCustomFieldFromWorkflow(workflowIndex, fieldIndex) {
      if (this.workflows[workflowIndex].jobConfig.customFields && 
          this.workflows[workflowIndex].jobConfig.customFields[fieldIndex]) {
        this.workflows[workflowIndex].jobConfig.customFields.splice(fieldIndex, 1);
        this.saveWorkflow(workflowIndex);
      }
    },
    handleCustomFieldChange(workflowIndex, fieldIndex) {
      const mapping = this.workflows[workflowIndex].jobConfig.customFields[fieldIndex];
      const fieldDef = this.availableCustomFields.find(f => f.uuid === mapping.fieldUuid);
      
      if (fieldDef) {
        mapping.name = fieldDef.name;
        mapping.fieldName = fieldDef.fieldName;
        mapping.isNative = fieldDef.isNative;
        mapping.fieldType = fieldDef.type;
      }
      
      this.saveWorkflow(workflowIndex);
    },
    isCustomFieldSelected(workflowIndex, fieldUuid, currentUuid) {
      if (!this.workflows[workflowIndex].jobConfig.customFields) return false;
      return this.workflows[workflowIndex].jobConfig.customFields.some(f => f.fieldUuid === fieldUuid && f.fieldUuid !== currentUuid);
    },
    isAttachmentFieldChecked(workflowIndex, fieldUuid) {
      const config = this.workflows[workflowIndex].jobConfig;
      if (!config.attachments) return false;
      return config.attachments.some(a => a.fieldUuid === fieldUuid);
    },
    toggleAttachmentField(workflowIndex, fieldUuid) {
      const config = this.workflows[workflowIndex].jobConfig;
      if (!config.attachments) config.attachments = [];
      
      const index = config.attachments.findIndex(a => a.fieldUuid === fieldUuid);
      if (index > -1) {
        config.attachments.splice(index, 1);
      } else {
        config.attachments.push({
          id: `att-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          fieldUuid: fieldUuid
        });
      }
      this.saveWorkflow(workflowIndex);
    },
    handleFieldDragStart(event, fieldKey) {
      try {
        event.dataTransfer.setData('text/plain', `{${fieldKey}}`);
        event.dataTransfer.setData('application/merge-field', fieldKey);
        event.dataTransfer.effectAllowed = 'move';
      } catch (e) {
        console.error('Error setting drag data:', e);
      }
      
      event.target.style.opacity = '0.5';
      event.target.classList.add('dragging');
    },
    handleFieldDragEnd(event) {
      event.target.style.opacity = '1';
      event.target.classList.remove('dragging');
    }
  }
};
</script>

<style scoped>
/* Main Layout */
.job-mapping-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.job-mapping-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background: #f1f5f9;
  min-height: calc(100vh - 64px);
  position: relative;
}

.job-field-mapping-hint {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  color: #0369a1;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.45;
}

.workflow-main-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.workflow-row-modern {
  cursor: pointer;
  transition: background-color 0.2s;
}

.workflow-row-modern:hover {
  background-color: #f8fafc;
}

.editing-row {
  background-color: #f0f9ff !important;
  border-left: 4px solid #24acfe;
}

/* Header and Search */
.workflows-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.search-container-large {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-input-large {
  width: 100%;
  padding: 10px 16px 10px 40px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 15px;
  outline: none;
  transition: all 0.2s;
}

.search-input-large:focus {
  border-color: #24acfe;
  box-shadow: 0 0 0 3px rgba(36, 172, 254, 0.1);
}

.search-icon-inside {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

/* Buttons */
.btn-add-workflow-large {
  padding: 10px 24px;
  background: linear-gradient(135deg, rgb(0, 157, 255) 0%, rgb(0, 227, 255) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.3s;
  box-shadow: 0 4px 6px rgba(36, 172, 254, 0.2);
}

.btn-add-workflow-large:hover {
  filter: brightness(0.9);
}

.btn-table-action {
  padding: 6px 16px;
  background: white;
  color: #24acfe;
  border: 1px solid #24acfe;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 80px;
}

.btn-table-action:hover {
  background: #f0f9ff;
  box-shadow: 0 2px 4px rgba(36, 172, 254, 0.1);
}

.btn-more-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  background: white;
  color: #24acfe;
  border: 1px solid #24acfe;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-more-actions:hover {
  background: #f0f9ff;
}

/* Dropdown */
.dropdown-wrapper {
  position: relative;
  display: inline-block;
}

.dropdown-menu-modern {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 5px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 140px;
  overflow: hidden;
}

.dropdown-item-modern {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: #475569;
  font-size: 14px;
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;
}

.dropdown-item-modern:hover {
  background: #f8fafc;
}

.dropdown-item-modern.delete {
  color: #ef4444;
}

.dropdown-item-modern.delete:hover {
  background: #fff1f2;
}

/* Table */
.workflows-table-container-modern {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.workflows-list-table-modern {
  width: 100%;
  border-collapse: collapse;
}

.workflows-list-table-modern th {
  background: #f5f5f5;
  padding: 15px 20px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e0e0e0;
}

.workflows-list-table-modern td {
  padding: 15px 20px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  color: #1e293b;
}

.status-cell-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.workflow-status-cell {
  padding: 15px 20px;
}

.action-buttons-modern {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}

.workflows-list-table-modern th.text-right {
  text-align: right;
  padding-right: 20px;
}

.workflows-list-table-modern td.text-right {
  text-align: right;
  padding-right: 20px;
}

/* Edit Section */
.workflow-edit-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #f0f0f0;
}

.workflow-footer {
  display: flex;
  justify-content: center;
  padding: 24px 0 8px;
  border-top: 1px solid #e2e8f0;
  margin-top: 24px;
}

.btn-done-editing {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 32px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(16, 185, 129, 0.2);
}

.btn-done-editing:hover {
  background: #059669;
  box-shadow: 0 6px 12px rgba(16, 185, 129, 0.3);
  transform: translateY(-2px);
}

.btn-done-editing:active {
  transform: translateY(0);
}

.workflow-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  min-width: 0;
}

.workflow-header {
  padding: 20px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.workflow-title {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  flex: 1;
}

.workflow-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.workflow-name-input-rich {
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #1e293b;
  max-width: 400px;
}

.workflow-badge-modern {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.workflow-badge-modern.enabled {
  background: #e6fcf5;
  color: #0ca678;
}

.workflow-badge-modern.disabled {
  background: #fff5f5;
  color: #fa5252;
}

/* Workflow Content & Sections */
.workflow-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #f8fafc;
}

.workflow-section {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.workflow-section h4 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  border: none;
  padding: 0;
}

.job-config-section {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section-header {
  margin: 0 0 8px 0;
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
  border: none;
  padding: 0;
}

.section-header-with-action {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.section-header-with-action .section-header {
  margin: 0;
}

.btn-add-small {
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, rgb(0, 157, 255) 0%, rgb(0, 227, 255) 100%);
  border: 1px solid #24acfe;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.btn-add-small:hover {
  filter: brightness(0.9);
  box-shadow: 0 2px 4px rgba(36, 172, 254, 0.2);
}

/* Form Grid & Fields */
.config-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.config-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.config-field.full-width {
  grid-column: span 2;
}

.field-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}

.field-label {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.field-description {
  font-size: 12px;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
}

/* Preview Button */
.btn-preview-icon {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 2px 4px !important;
  background: white !important;
  color: #24acfe !important;
  border: 1px solid #24acfe !important;
  border-radius: 3px !important;
  cursor: pointer !important;
  transition: all 0.2s !important;
  width: auto !important;
  height: auto !important;
  min-width: 18px !important;
  min-height: 18px !important;
  font-size: 10px !important;
  line-height: 1.2 !important;
  flex-shrink: 0 !important;
}

.btn-preview-icon:hover {
  background: #f0f9ff !important;
  color: #1e90d6 !important;
  border-color: #1e90d6 !important;
}

.btn-preview-icon.active {
  color: white !important;
  background: #24acfe !important;
  border-color: #24acfe !important;
}

.btn-preview-icon.active:hover {
  color: white !important;
  background: #24acfe !important;
  border-color: #24acfe !important;
}

.btn-preview-icon svg {
  width: 12px !important;
  height: 12px !important;
  flex-shrink: 0 !important;
}

/* Contact & Nested Sections */
.contact-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
}

.contact-header {
  padding: 12px 20px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.contact-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-trash-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: white;
  color: #ef4444;
  border: 1px solid #fee2e2;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-trash-icon:hover {
  background: #fef2f2;
  color: #b91c1c;
  border-color: #fecaca;
}

.btn-trash-icon svg {
  width: 16px;
  height: 16px;
}

.contact-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.contact-content {
  padding: 20px;
}

.contact-fields-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.btn-add-contact {
  padding: 6px 12px;
  background: linear-gradient(135deg, rgb(0, 157, 255) 0%, rgb(0, 227, 255) 100%);
  border: 1px solid #24acfe;
  color: white;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 4px rgba(36, 172, 254, 0.2);
}

.btn-add-contact:hover {
  filter: brightness(0.9);
  box-shadow: 0 2px 4px rgba(36, 172, 254, 0.2);
}

.btn-add-contact:active {
  transform: scale(0.98);
}

/* Checklists, Notes, Custom Fields */
.checklist-item, .note-item, .custom-field-item {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.checklist-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.checklist-item-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
}

.checklist-item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.btn-delete-item {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: transparent;
  border: none;
  color: #ef4444;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;
}

.btn-delete-item:hover {
  background: #fef2f2;
  color: #dc2626;
}

.btn-settings-small {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: transparent;
  border: none;
  color: #f97316;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;
  font-size: 16px;
}

.btn-settings-small:hover {
  background: #fff7ed;
  color: #ea580c;
}

.checklist-item-row:last-child {
  border-bottom: none;
}

.btn-add-item {
  margin-top: 12px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #24acfe;
  background: white;
  border: 1px solid #24acfe;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
}

.btn-add-item:hover {
  background: #f0f9ff;
  box-shadow: 0 2px 4px rgba(36, 172, 254, 0.1);
}

/* Attachment Options */
.attachment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.attachment-checkbox-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.attachment-checkbox-item:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.attachment-name {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  flex: 1;
}

.attachment-model {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
}

/* Available Fields Panel */
.available-fields-panel {
  position: sticky !important;
  top: 20px !important;
  width: 380px !important;
  max-height: calc(100vh - 40px) !important;
  background: white !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 12px !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1) !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  z-index: 10 !important;
}

.fields-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.fields-panel-header h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.btn-refresh-small {
  padding: 6px;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-refresh-small:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.btn-refresh-small:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-refresh-small .loading {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.fields-panel-content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.fields-search {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
}

.fields-search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  transition: all 0.2s;
}

.fields-search-input:focus {
  border-color: #24acfe;
  box-shadow: 0 0 0 3px rgba(36, 172, 254, 0.1);
}

.fields-list {
  flex: 1;
  padding: 12px 16px;
  overflow-y: auto;
}

.fields-section {
  margin-bottom: 20px;
}

.fields-section:last-child {
  margin-bottom: 0;
}

.fields-section-title {
  margin: 0 0 10px 0;
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.fields-section-items {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.fields-list .fields-section .fields-section-items .field-bubble.draggable-field.field-with-tooltip,
.fields-section-items .field-bubble.draggable-field,
.fields-section-items .field-bubble,
.field-bubble.draggable-field.field-with-tooltip,
.field-bubble.draggable-field,
.field-bubble {
  display: inline-flex !important;
  align-items: center !important;
  gap: 2px !important;
  padding: 2px 4px !important;
  background-color: white !important;
  font-size: 10px !important;
  line-height: 1.2 !important;
  background: white !important;
  border: 1px solid #24acfe !important;
  border-radius: 6px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #24acfe !important;
  cursor: grab !important;
  user-select: none !important;
  transition: all 0.2s !important;
  position: relative !important;
}

.fields-list .fields-section .fields-section-items .field-bubble.draggable-field.field-with-tooltip:hover,
.fields-section-items .field-bubble.draggable-field:hover,
.fields-section-items .field-bubble:hover,
.field-bubble.draggable-field.field-with-tooltip:hover,
.field-bubble.draggable-field:hover,
.field-bubble:hover {
  background-color: #f0f9ff !important;
  background: #f0f9ff !important;
  box-shadow: 0 2px 4px rgba(36, 172, 254, 0.1) !important;
}

.field-bubble:active,
.field-bubble.dragging {
  cursor: grabbing;
  opacity: 0.6;
  transform: scale(0.95);
}

.draggable-field {
  cursor: grab;
}

.draggable-field:active {
  cursor: grabbing;
}

.field-bubble.hidden {
  display: none;
}

/* Available Fields Tooltip - Horizontal Layout - UPDATED */
.fields-list {
  position: relative !important;
}

.fields-list .fields-section .fields-section-items .field-bubble.field-with-tooltip {
  position: relative !important;
}

/* TOOLTIP HORIZONTAL FIX - TEST VERSION */
.fields-list .fields-section .fields-section-items .field-bubble.field-with-tooltip .tooltip-text {
  visibility: hidden !important;
  position: absolute !important;
  left: calc(100% + 8px) !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  margin-left: 0 !important;
  margin-bottom: 0 !important;
  bottom: auto !important;
  padding: 6px 10px !important;
  background: #4b5563 !important;
  color: white !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  border-radius: 6px !important;
  white-space: nowrap !important;
  max-width: 300px !important;
  word-break: break-word !important;
  z-index: 99999 !important;
  opacity: 0 !important;
  transition: opacity 0.2s !important;
  pointer-events: none !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

/* Show tooltip on hover */
.fields-list .fields-section .fields-section-items .field-bubble.field-with-tooltip:hover .tooltip-text {
  visibility: visible !important;
  opacity: 1 !important;
}

/* Position tooltip on left if it would overflow container (last 3 buttons in row) */
.fields-list .fields-section .fields-section-items .field-bubble:last-child .tooltip-text,
.fields-list .fields-section .fields-section-items .field-bubble:nth-last-child(2) .tooltip-text,
.fields-list .fields-section .fields-section-items .field-bubble:nth-last-child(3) .tooltip-text {
  left: auto !important;
  right: calc(100% + 8px) !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

/* Arrow pointing to button (right side tooltip) */
.fields-list .fields-section .fields-section-items .field-bubble.field-with-tooltip .tooltip-text::after {
  content: '' !important;
  position: absolute !important;
  left: -5px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  border: 5px solid transparent !important;
  border-right-color: #1e293b !important;
}

/* Arrow for tooltips - right side (default) */
.tooltip-text.tooltip-horizontal::after {
  content: '' !important;
  position: absolute !important;
  left: -5px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  border: 5px solid transparent !important;
  border-right-color: #4b5563 !important;
}

/* Arrow for left-positioned tooltips */
.tooltip-text.tooltip-horizontal.tooltip-left::after {
  left: auto !important;
  right: -5px !important;
  border-right-color: transparent !important;
  border-left-color: #4b5563 !important;
}

/* Select & Input field styling */
.select-field, .config-select, .config-input, .modal-select, .modal-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  background-color: white;
  transition: all 0.2s;
  color: #1e293b;
}

.select-field:focus, .config-select:focus, .config-input:focus {
  border-color: #24acfe;
  box-shadow: 0 0 0 3px rgba(36, 172, 254, 0.1);
}

.config-description {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.toggle-switch-inline {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  margin: 0;
}

.toggle-switch input,
.toggle-switch-inline input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
  pointer-events: none;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: .4s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

input:checked + .toggle-slider {
  background-color: #24acfe;
}

input:checked + .toggle-slider:before {
  transform: translateX(20px);
}
.select-field.invalid-field {
  border-color: #ef4444;
  background-color: #fef2f2;
}

.select-field.invalid-field:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.workflow-editor-container {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) 380px !important;
  gap: 24px !important;
  align-items: start !important;
  width: 100% !important;
}

.header-status-group {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.btn-close-save {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: linear-gradient(135deg, rgb(0, 157, 255) 0%, rgb(0, 227, 255) 100%);
  color: white;
  border: 1px solid #24acfe;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(36, 172, 254, 0.2);
}

.btn-close-save:hover {
  filter: brightness(0.9);
  box-shadow: 0 2px 4px rgba(36, 172, 254, 0.2);
}

.btn-close-save:active {
  transform: scale(0.98);
}

/* Checklist Item Settings Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.modal-title-group h3 {
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
}

.modal-title-group p {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 28px;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #e2e8f0;
  color: #475569;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-field {
  margin-bottom: 20px;
}

.modal-field:last-child {
  margin-bottom: 0;
}

.modal-field label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.modal-input,
.modal-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
  background: white;
  color: #1e293b;
}

.modal-input:focus,
.modal-select:focus {
  border-color: #24acfe;
  box-shadow: 0 0 0 3px rgba(36, 172, 254, 0.1);
}

.modal-select {
  cursor: pointer;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.btn-modal-cancel {
  padding: 10px 20px;
  background: white;
  color: #475569;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-modal-cancel:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.btn-modal-save {
  padding: 10px 20px;
  background: linear-gradient(135deg, rgb(0, 157, 255) 0%, rgb(0, 227, 255) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(36, 172, 254, 0.2);
}

.btn-modal-save:hover {
  filter: brightness(0.9);
  transform: translateY(-1px);
}

.btn-modal-save:active {
  transform: translateY(0);
}
</style>

<style>
/* Force field-bubble styles to match edit button - non-scoped for maximum override */
.fields-list .fields-section .fields-section-items .field-bubble.draggable-field.field-with-tooltip,
.fields-section-items .field-bubble.draggable-field,
.fields-section-items .field-bubble,
.field-bubble.draggable-field.field-with-tooltip,
.field-bubble.draggable-field,
.field-bubble,
.white-field-bubble {
  display: inline-flex !important;
  align-items: center !important;
  gap: 2px !important;
  padding: 2px 4px !important;
  background-color: #ffffff !important;
  font-size: 10px !important;
  line-height: 1.2 !important;
  background: #ffffff !important;
  border: 1px solid #24acfe !important;
  border-radius: 3px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #24acfe !important;
  cursor: grab !important;
  user-select: none !important;
  transition: all 0.2s !important;
  position: relative !important;
}

.fields-list .fields-section .fields-section-items .field-bubble.draggable-field.field-with-tooltip:hover,
.fields-section-items .field-bubble.draggable-field:hover,
.fields-section-items .field-bubble:hover,
.field-bubble.draggable-field.field-with-tooltip:hover,
.field-bubble.draggable-field:hover,
.field-bubble:hover,
.white-field-bubble:hover {
  background-color: #f0f9ff !important;
  background: #f0f9ff !important;
  box-shadow: 0 2px 4px rgba(36, 172, 254, 0.1) !important;
}
</style>
